/* eslint-disable no-param-reassign */
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Accordion, AccordionDetails, AccordionSummary, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, InputLabel, MenuItem, Select, Typography } from '@mui/material'
import { useAuthContext } from 'AuthProvider'
import { FileDownloadData, MasterSearchFrom, SearchBox } from 'components/commonparts'
import { DataList, SearchCondition, TransportSummary } from 'components/parts/OrderPlanTransportLot'
import { subDays, addDays } from 'date-fns'
import { useCalcStock, useCalcStockParams, handleStockCalculation } from 'hooks/useCalcStock'
import { useCenter } from 'hooks/useCenter'
import { useCenterSupplier } from 'hooks/useCenterSupplier'
import { useGridSearch } from 'hooks/useGridSearch'
import { useOrderPlanTransportLot } from 'hooks/useOrderPlanTransportLot'
import { useOrderQuantityOptimize, type StockList as OrderQuantityStockList } from 'hooks/useOrderQuantityOptimize'
import { useStock } from 'hooks/useStock'
import { useTransportLot } from 'hooks/useTransportLot'
import { useTransportLotOptimize } from 'hooks/useTransportLotOptimize'
import { useTransportType } from 'hooks/useTransportType'
import { useSearchParamStore } from 'store/searchParamStore'
import { CompactAlertBox } from 'style'
import { initRecordEditEx } from 'types/dataGridTypes'
import { convertDateToRequestParam, isEqualDate, normalizeDate } from 'utils/convertFromLocalDate'
import { getDsModelAPI } from 'utils/getDsModelAPI';
import { getInitCompanyId } from 'utils/getInitParam'
import { getLocalSearchParam } from 'utils/getLocalSearchParam'

import type { RowClassParams } from 'ag-grid-community'
import type { GetStockStockGetRequest, MstSupplier, MstCenterSupplier, ResponseResult, Stock, StockUpdateBase, Daylist, MstCenter } from 'api-client'
import type { OrderPlan, OrderPlanStock, TransportSummaryData } from 'components/parts/OrderPlanTransportLot/type'
import type { OptimizeTransportLotRequestTodayStockListInner, Transport, TransportLotPlan, TransportLotRequest } from 'ds-model-api'
import type { MstTransportTypesSeq } from 'hooks/useTransportType'
import type { OrderPlanTransportLotRequest } from 'store/searchParamStore'

// Calculate number of pallets based on lot quantity and max pallet quantity
const calculateLotQuantityPallet = (lotQuantity: number, maxPalletQuantity: number): number => {
  if (!maxPalletQuantity || maxPalletQuantity === 0) return 0
  return lotQuantity / maxPalletQuantity
}

// Calculate total weight of lot items in tons
const calculateLotItemWeight = (lotQuantity: number, weight: number): number => {
  if (!lotQuantity || !weight) return 0
  return (lotQuantity * weight) / 1000000
}

// Group stock data by receipt date and prepare for transport lot calculation (DS API)
function groupStockDataByRecDate(
  stockList: Stock[],
  minLoadCapacity: number,
  transports: Transport[]
): TransportLotRequest[] {
  const groupedByDate: Record<string, Stock[]> = {}

  stockList.forEach((stock) => {
    if (!stock.recDate) return
    const dateKey = normalizeDate(stock.recDate)?.toLocaleDateString('sv-SE')
    if (!dateKey) return
    if (!groupedByDate[dateKey]) {
      groupedByDate[dateKey] = []
    }
    groupedByDate[dateKey].push(stock)
  })

  const validTransports = transports
    .filter((transport) => transport?.transportTypeId && transport.maxWeight && transport.maxPalletCount)
    .map((transport) => ({
      transportTypeId: transport.transportTypeId.toString(),
      maxWeight: transport.maxWeight,
      maxPalletCount: transport.maxPalletCount,
    }))

  return Object.entries(groupedByDate)
    .map(([recDateStr, stocks]) => {
      const validItems = stocks
        .filter((stock) => stock.itemCode && stock.arrivalQuantity && stock.weight && stock.maxPalletQuantity)
        .map((stock) => ({
          itemCode: stock.itemCode!,
          orderQuantity: stock.arrivalQuantity!,
          weight: stock.weight!,
          maxPalletQuantity: stock.maxPalletQuantity!,
        }))

      if (validItems.length === 0) return null

      return {
        recDate: normalizeDate(recDateStr),
        items: validItems,
        minLoadCapacity: Number(minLoadCapacity),
        transports: validTransports,
      }
    })
    .filter((item): item is TransportLotRequest => item !== null)
}

// make transport lot by day: transportLotNo, recDate, transportTypeId, maxWeight, maxPalletCount, transportLotNo, lotItems[array]
function groupDayOrdersByTransportLot(
  orderPlanListByDay: OrderPlan[],
){
  const stransportLots = new Map<string, any>()

  orderPlanListByDay.forEach((item) => {
    const key = `${item.transportLotNo}`

    if (!stransportLots.has(key)) {
      stransportLots.set(key, {
        recDate: item.recDate ? convertDateToRequestParam(normalizeDate(item.recDate)!) : null,
        transportTypeId: String(item.orderTransportTypeId || ''),
        maxWeight: item.maxWeight,
        maxPalletCount: item.maxPalletCount,
        transportLotNo: item.transportLotNo,
        lotItems: []
      })
    }

    stransportLots.get(key)!.lotItems.push({
      itemCode: item.itemCode,
      lotQuantity: item.lotQuantity
    })
  })

  return Array.from(stransportLots.values())
}

function updateOrderPlanListByTransportLot(
  stockListUpdate: OrderPlanStock[],
  orderPlanList: OrderPlan[],
  transportLotList: TransportLotPlan[],
  minLoadCapacity: number,
  transportTypeSearch: MstTransportTypesSeq
) {
  // get recDate today transport
  const targetRecDates = new Set(
    transportLotList.map((t) =>
      normalizeDate(t.recDate)?.toLocaleDateString('sv-SE')
    )
  );

  // Remove items with recDate in today transport lots (Delete -> insert)
  const orderPlanListNew = orderPlanList.filter((item) => {
    const date = normalizeDate(item.recDate!)?.toLocaleDateString('sv-SE')
    return !targetRecDates.has(date)
  })

  if (transportLotList[0].lotQuantity === 0) {
    return orderPlanListNew
  }
  // For each target recDate, generate new orderPlan from stockList
    transportLotList
      .forEach((transportLot) => {
          // get new stock
          stockListUpdate.
            filter((stockItem) => {
              const itemDateStr = normalizeDate(transportLot.recDate!)?.toLocaleDateString('sv-SE');
              const stockDateStr = normalizeDate(stockItem.recDate!)?.toLocaleDateString('sv-SE'); 

              return transportLot.itemCode === stockItem.itemCode 
                && itemDateStr === stockDateStr
              }
            )
            // make new order plan
            .forEach((stockItem)=> {
              const orderPlan: OrderPlan = {
                ...initRecordEditEx(),
                companyId: stockItem.companyId || 0,
                centerId: stockItem.centerId || 0,
                orgSupplierId: stockItem.orgSupplierId || '',
                recDate: stockItem.recDate,
                orderDate: stockItem.orderDate,
                transportLotNo: transportLot.transportLotNo || null,
                itemCode: transportLot.itemCode || '',
                orgItemCode: stockItem.orgItemCode || '',
                itemName:stockItem?.itemName || '',
                expectedRank: stockItem?.expectedRank ?? null,
                isExpectedRankAlert: stockItem?.isExpectedRankAlert ?? false,
                orderQuantityRec: stockItem?.orderQuantityRec ?? null,
                lotQuantity: transportLot.lotQuantity || 0,
                maxPalletQuantity: stockItem.maxPalletQuantity || 0,
                lotQuantityPallet: calculateLotQuantityPallet(
                  transportLot.lotQuantity || 0,
                  stockItem.maxPalletQuantity || 0
                ),
                lotItemWeight: calculateLotItemWeight(transportLot.lotQuantity || 0, stockItem.weight || 1),
                stockDays: stockItem?.stockDays ?? 0,
                stockDaysInfluence: (stockItem?.stockDays ?? 0) - (stockItem?.initialStockDays ?? 0),
                orderDatePrev:  null,
                remarks: stockItem.remarks || '',
                lotQuantityPrev:  null,
                transportLotWeight: stockItem.weight || 0,
                weight: stockItem.weight || 0,
                orderTransportTypeId: Number(transportLot.transportTypeId),
                ...(() => {
                  const transportType = transportTypeSearch?.list?.find(
                    (type: any) => type.transportTypeId === Number(transportLot.transportTypeId)
                  )
                  return {
                    transportTypeName: transportType?.transportTypeName || '',
                    maxWeight: transportType?.maxWeight || 0,
                    maxPalletCount: transportType?.maxPalletCount || 0,
                  }
                })(),
                minLoadCapacity,
                stockDaysSetting: stockItem.stockDaysSetting || 0,
                isOrderConfirmed: stockItem.isOrderConfirmed || false,
                centerName: stockItem.centerName || '',
                supplierName: stockItem.supplierName || '',
                orgCenterId: stockItem?.orgCenterId || '',
                isUpdatedArrival: stockItem.isUpdatedArrival || false,
              }
            
            orderPlanListNew.push(orderPlan);
        })
      });

  return orderPlanListNew;
}

function updateStockListByTransportLot(
  stockList: OrderPlanStock[],
  optimizeStockList: OptimizeTransportLotRequestTodayStockListInner[]
) {
  return stockList.map((item) => {
    const matched = optimizeStockList?.find((stock) =>
      item.itemCode === stock.itemCode && isEqualDate(item.recDate, stock.recDate)
    );

    if (matched) {
      return {
        ...item,
        arrivalQuantity: matched.arrivalQuantityPlan,
        arrivalQuantityPlan: matched.arrivalQuantityPlan,
        avgShipExpected: matched.avgShipExpected,
        caseLot: matched.caseLot,
        maxPalletQuantity: matched.maxPalletQuantity,
        stockDays: matched.stockDays,
        stockQuantity: matched.stockQuantity,
        stopArrival: matched.stopArrival,
        weight: matched.weight,
      };
    }

    return item;
  });
}

// Helper function to mark moved items with isEdited and isOrderDateChange flags (発注日積載率調整時に使用)
function updateOrderPlanListWithMovedItems(
  orderPlanList: OrderPlan[],
  movedItems?: Array<{
    orderDate: Date;
    newOrderDate: Date;
    itemCode: string;
    moveQuantity: number;
  }>
): OrderPlan[] {
  if (!movedItems || movedItems.length === 0) {
    return orderPlanList;
  }

  return orderPlanList.map((orderPlan) => {
    // Find if this order plan item was moved
    const movedItem = movedItems.find((moved) =>
      orderPlan.itemCode === moved.itemCode && (isEqualDate(orderPlan.recDate, moved.newOrderDate) || isEqualDate(orderPlan.recDate, moved.orderDate))
    );

    if (movedItem) {
      // Mark as edited and order date changed
      const editedFields = new Set(orderPlan.editedFields || []);
      editedFields.add('lotQuantity');
      
      return {
        ...orderPlan,
        isEdited: true,
        isOrderDateChange: true,
        editedFields,
      };
    }

    return orderPlan;
  });
}


interface TransportTypeUrl {
  transportTypeId: number;
}

// Get Search Url Params
function getSearchUrl(
  param: GetStockStockGetRequest,
  transportTypeList: TransportTypeUrl[] = [],
  localMinLoadCapacity?: number
): string {
  const transportTypeIdList = transportTypeList
    .map(item => item.transportTypeId)
    .join(',');

  const query = new URLSearchParams({
    company_id: String(param.companyId),
    center_id: String(param.centerId),
    org_supplier_id: String(param.orgSupplierId),
    transport_type: transportTypeIdList,
    ...(localMinLoadCapacity !== undefined && localMinLoadCapacity !== 0 && { 
      min_load_capacity: String(localMinLoadCapacity) 
    })
  });

  return `?${query.toString()}`;
}

function showResultMessage(
  setResultMessage: (msg: ResponseResult | undefined) => void,
  options: { status: 'success' | 'warning' | 'error'; message: string }
) {
  setResultMessage({
    status: options.status,
    message: options.message,
    pageNo: 0,
    systemDate: normalizeDate(Date.now()),
    systemInfo: {
      systemDate: null,
      sysCurrentStatus: [],
      sysProcessingCount: 0,
      companyStatus: '',
      systemStatus: ''
    }
  });
}

// Map rectypeName to UI format
function mapRectypeNameToUI(rectypeName: string): '実績' | '当日' | '予定' {
  switch (rectypeName) {
    case '実績': return '実績';
    case '本日': return '当日';
    case '予測': return '予定';
    default: return '予定';
  }
}

// Main component for order plan transport lot management
function OrderPlanTransportLot() {
  // Hooks
  const { loginUserInfo, typeCode } = useAuthContext()
  const defaultOptimizeRange = typeCode
    ?.find(group => group.typeGroup === 'optimize_range')?.codes
    .find(code => code.defaultFlg)?.typeCode
  const { search } = useLocation()
  const query = new URLSearchParams(search)
  const { fetchOrderPlanTransportLotUpdate, result: orderPlanUpdateResult } = useOrderPlanTransportLot()
  const {
    stockData: stockDataResponse,
    fetchStockData,
    isLoading,
    isUpdating: isUpdatingStockCenter,
    result: stockDataResult,
    fetchUpdateStockCenter,
  } = useStock()
  const { calculateTransportLot, isLoading: isLoadingTransportLot } = useTransportLot()
  const { transportTypeSearch, fetchTransportTypeSearch } = useTransportType()
  const { optimizeTransportLot, isLoading: isLoadingOptimizeData, result: optimizeResult  } = useTransportLotOptimize()
  const { calculateShipQuantityStockBase, calculateStockQuantity, calculateAvgShipQuantityStockBase, calculateStockDays } = useCalcStock()
  const { optimizeOrderQuantity, isLoading: isOptimizingOrderQuantity, result: optimizeOrderQuantityResult } = useOrderQuantityOptimize()
  const { centerSupplierSearchResult, centerSupplierSearch , fetchCenterSupplierSearch, centerSupplierSearchIsLoading } = useCenterSupplier()
  const { centerSearch, fetchCenterSearch } = useCenter()

  const searchParamState = useSearchParamStore((state) => state.searchParamState)
  const setSearchParamState = useSearchParamStore((state) => state.setSearchParamState)
  const navigate = useNavigate()

  // Ref to track if initial data has been fetched
  const initialDataFetched = useRef(false)
  const shouldRunTransportLot = useRef(false)
  const isOptimizeFetching = useRef(false)
  const suppressMismatchCompare = useRef(false)

  // Derived State
  const trQueryParam = {
    companyId: Number(query.get('company_id')),
    centerId: Number(query.get('center_id')),
    orgSupplierId: query.get('org_supplier_id'),
    transportType: query.get('transport_type'),
    minLoadCapacity: query.get('min_load_capacity'),
    optimizeLoad: query.get('optimize_load'),
  }


  const localSearchParam = getLocalSearchParam(searchParamState, loginUserInfo?.tenantId, loginUserInfo?.companyId)
  const localCompanyId = localSearchParam?.orderPlanTransportLot?.companyId || undefined
  const localCenterId = localSearchParam?.orderPlanTransportLot?.centerId || undefined
  const localOrgSupplierId = localSearchParam?.orderPlanTransportLot?.orgSupplierId || undefined
  const localTransportType = localSearchParam?.orderPlanTransportLot?.transportType || undefined
  const queryTransportType = trQueryParam.transportType
    ? Object.fromEntries(
      trQueryParam.transportType
        .split(',')
        .map((id) => Number(id))
        .filter((id) => Number.isFinite(id) && id > 0)
        .map((id, idx) => [idx, id])
    )
    : undefined
  const initialTransportType = queryTransportType && Object.keys(queryTransportType).length > 0
    ? queryTransportType
    : localTransportType
  const localViewUnitType = localSearchParam?.orderPlanTransportLot?.viewUnitType || 'case'
  const localMinLoadCapacity = localSearchParam?.orderPlanTransportLot?.localMinLoadCapacity || undefined
  
  const initialMinLoadCapacity = trQueryParam.minLoadCapacity 
    ? Number(trQueryParam.minLoadCapacity) 
    : localMinLoadCapacity || 0

  const initSearchParam: GetStockStockGetRequest = {
    companyId: getInitCompanyId(trQueryParam.companyId, localCompanyId, loginUserInfo ? loginUserInfo.companyId : 0),
    centerId: trQueryParam.centerId ? trQueryParam.centerId : localCenterId,
    orgSupplierId: trQueryParam.orgSupplierId ? trQueryParam.orgSupplierId : localOrgSupplierId,
    viewUnitType: localViewUnitType,
    orderPlanFlg: true,
  }

  const calcParams = useCalcStockParams(
    loginUserInfo?.systemDate,
    initSearchParam.centerId,
    initSearchParam.companyId,
    centerSearch
  )
  
  // State Management
  const [downloadColDef, setDownloadColDef] = useState<any[] | undefined>([])
  const [hasGridEdited, setHasGridEdited] = useState<boolean>(false)
  const [updateTrigger, setUpdateTrigger] = useState<boolean | undefined>(undefined)
  const [resultMessage, setResultMessage] = useState<ResponseResult | undefined>(undefined)
  const [gridMessage, setGridMessage] = useState<ResponseResult | undefined>(undefined)
  const [uploadData, setUploadData] = useState<any[]>([])
  const [searchTrigger, setSearchTrigger] = useState<boolean>(false)
  const [calculateTransportType, setCalculateTransportType] = useState<boolean>(false)
  const [discardChangeDialogOpen, setDiscardChangeDialogOpen] = useState<boolean>(false)
  const [transportTypeData, setTransportTypeData] = useState<any>([])
  const [minLoadCapacity, setMinLoadCapacity] = useState<number>(initialMinLoadCapacity)
  const [centerSupplierInfo, setCenterSupplierInfo] = useState<MstSupplier | undefined>()
  const [centerSupplierDetail, setCenterSupplierDetail] = useState<MstCenterSupplier | undefined>()
  const [optimizeLoadFactorLogic, setOptimizeLoadFactorLogic] = useState<any | null | undefined>(0)
  const [searchParam, setSearchParam] = useState<GetStockStockGetRequest>(initSearchParam)
  const [loadingState, setLoadingState] = useState<boolean>(false)
  const [stockList, setStockList] = useState<OrderPlanStock[]>()
  const [orderPlanList, setOrderPlanList] = useState<OrderPlan[]>()
  const [orderPlanRecDates, setOrderPlanRecDates] = useState<Date[]>([])
  const [orderPlanOrderDates, setOrderPlanOrderDates] = useState<Date[]>([])
  const [downloadGridRows, setDownloadGridRows] = useState<OrderPlan[]>([])
  const [downloadMode, setDownloadMode] = useState<string>('csv')
  const [transportTypeDialogOpen, setTransportTypeDialogOpen] = useState(false)
  const [selectedRecDate, setSelectedRecDate] = useState<Date | null>(null)
  const [selectedTransportLotNo, setSelectedTransportLotNo] = useState<number | null>(null)
  const [selectedTransportTypeId, setSelectedTransportTypeId] = useState<number | null>(null)
  const [firstTimeFetchStock, setFirstTimeFetchStock] = useState<boolean>(false)
  const [transportSummaryList, setTransportSummaryList] = useState<TransportSummaryData[]>();
  const [isOpen, setIsOpen] = useState(false);
  const [validationFunction, setValidationFunction] = useState<((showErrors?: boolean) => boolean) | null>(null)
  const [searchFormLastUpdateDatetime, setSearchFormLastUpdateDatetime] = useState<Date | null>(null)
  const [dayList, setDayList] = useState<any[]>([])
  const [optimizeRangeEndDate, setOptimizeRangeEndDate] = useState<Date | null>(null)
  const [mismatchedStockItemCodes, setMismatchedStockItemCodes] = useState<string[]>([])
  
  // Search functionality using custom hook
  const gridSearch = useGridSearch({ autoShowOnCtrlF: true })


  // Check min_load_capacity_flg and show warning if not all lots meet the requirement
  function updateMinLoadCapacityStatus(transportLots: any[]) {
    const isAllMinLoadCapacity = transportLots.every(lot => lot.minLoadCapacityFlg === true)
    if (!isAllMinLoadCapacity) {
      showResultMessage(setResultMessage, {
        status: 'warning',
        message: 'すべての積載率を調整することはできませんでした。'
      })
    }
    else {
      showResultMessage(setResultMessage, {
        status: 'success',
        message: '積載率調整が完了しました'
      })
    }
  }

  // Calculate days between systemDate and target date
  const calculateDaysDifference = (systemDate: Date, targetDate: Date): number => {
    // Normalize both dates to midnight (0:00:00)
    const date1 = new Date(systemDate)
    date1.setHours(0, 0, 0, 0)
    
    const date2 = new Date(targetDate)
    date2.setHours(0, 0, 0, 0)
    
    const timeDiff = date2.getTime() - date1.getTime()
    return Math.floor(timeDiff / (1000 * 60 * 60 * 24))
  }

  // Fetch optimize range dates then fetch stock data with calculated date range
  const fetchStockWithOptimizeRange = async (requestParam: GetStockStockGetRequest) => {
    try {
      const systemDate = normalizeDate(loginUserInfo?.systemDate ?? Date.now())!
      systemDate.setHours(0, 0, 0, 0)
      
      // Step 1: Get optimize range dates
      const rangeData = await fetchStockData({
        ...requestParam,
        getPeriodDataFlg: true
      })
      
      // Step 2: Calculate date range from optimize dates
      let endDateTypeCode = '13'
      
      if (rangeData?.optimizeRangeEndDate) {
        // const startDate = normalizeDate(rangeData.optimizeRangeStartDate)
        const endDate = normalizeDate(rangeData.optimizeRangeEndDate)
        
        if (endDate) {
          // setOptimizeRangeStartDate(startDate)
          setOptimizeRangeEndDate(endDate)
          
          const endDays = calculateDaysDifference(systemDate, endDate)
          endDateTypeCode = String(endDays)
        }
      }
      else {
        return
      }
      
      // Step 3: Fetch stock data with calculated date range
      await fetchStockData({
        ...requestParam,
        endDateTypeCode
      })
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error fetching stock with optimize range:', error)
      // Fallback: fetch with default range
      await fetchStockData(requestParam)
    }
  }

  // Receive validation function from SearchCondition
  const handleValidateAndExecute = useCallback((executeFn: (showErrors?: boolean) => boolean) => {
    setValidationFunction(() => executeFn)
  }, [])

  // Initial data fetching - only run once when component mounts
  useEffect(() => {
    // Only fetch if have companyId and not already fetched
    if (!loginUserInfo?.companyId || initialDataFetched.current) return;
    
    
    const firstTimeRender = async () => {
      try {
        initialDataFetched.current = true;
        
        const promises: any[] = [
          fetchTransportTypeSearch({
          enabled: true,
          companyId: loginUserInfo.companyId
        }),
          fetchCenterSearch({
            companyId: loginUserInfo.companyId
          })
        ];
        if (initSearchParam.centerId && initSearchParam.orgSupplierId) {
          // Fetch with optimize range (2 API calls: get range, then get data)
          await fetchStockWithOptimizeRange(initSearchParam)
        }
        
        setFirstTimeFetchStock(true);
      } catch (error) {
        // Handle error silently
        initialDataFetched.current = false; // Reset on error
      }
    }
    firstTimeRender()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loginUserInfo?.companyId])

  useEffect(() => {
    if (shouldRunTransportLot.current &&
      stockList && stockList?.length > 0 &&
      orderPlanList && orderPlanList?.length > 0 &&
      orderPlanRecDates && orderPlanRecDates?.length > 0 &&
      minLoadCapacity && 
     (transportTypeSearch || (transportTypeData && Object.keys(transportTypeData).length > 0))
    ) {
      onTransportLot(false)
      shouldRunTransportLot.current = false
    }
  }, [orderPlanList, stockList])

  // Initialize stock list and order plan dates
  useEffect(() => {
    setLoadingState(true)
    const stockListPlan = handleStockCalculation(
      stockDataResponse?.list ?? [],
      stockDataResponse?.list ?? [],
      loginUserInfo,
      { calculateShipQuantityStockBase, calculateStockQuantity, calculateAvgShipQuantityStockBase, calculateStockDays },
      calcParams,
      true,
    )
    setStockList(stockListPlan)

    const systemDate = normalizeDate(loginUserInfo?.systemDate ?? Date.now())!
    systemDate.setHours(0, 0, 0, 0)
    
    const startDate = subDays(systemDate, 7)
    let endDate: Date
    if(optimizeRangeEndDate) {
      const endDays = calculateDaysDifference(systemDate, optimizeRangeEndDate)
      endDate = addDays(systemDate, endDays)
    } else {
      endDate = addDays(systemDate, 13)
    }

    const orderPlanDates: Date[] = (stockListPlan ?? []).reduce((acc: Date[], rec) => {
      if (rec.recDate && !acc.some((item) => isEqualDate(item, rec.recDate)) && rec.recDate <= endDate && rec.arrivalQuantity && rec.arrivalQuantity > 0) {
        acc.push(rec.recDate)
      }
      return acc
    }, []).sort((a, b) => normalizeDate(a)!.getTime() - normalizeDate(b)!.getTime())

    setOrderPlanRecDates(orderPlanDates)
    
    // Get orderPlanOrderDates sorted list
    const orderDatesList: Date[] = (stockListPlan ?? []).reduce((acc: Date[], rec) => {
      if (rec.orderDate && !acc.some((item) => isEqualDate(item, rec.orderDate)) && rec.recDate && rec.recDate <= endDate && rec.arrivalQuantity && rec.arrivalQuantity > 0) {
        acc.push(rec.orderDate)
      }
      return acc
    }, []).sort((a, b) => normalizeDate(a)!.getTime() - normalizeDate(b)!.getTime())
    setOrderPlanOrderDates(orderDatesList)

    const daylist = stockDataResponse?.daylist || [];
    const newDayList = daylist.filter((item) =>
      item.orderDate &&
      item.datevalue &&
      item.datevalue >= systemDate
    ).map(item => ({
      datevalue: item.datevalue,
      rectype: item.rectype,
      rectypeName: item.rectypeName,
      orderDate: item.orderDate,
      isOrderConfirmed: item.isOrderConfirmed,
      isOrderToday: item.isOrderToday,
      isUpdatedArrivalTmp: item.isUpdatedArrivalTmp,
    }))
    setDayList(newDayList)

    const savedTransportLotList = stockDataResponse.transportLotList ?? []
    const orderPlanListData: OrderPlan[] = []

    orderPlanDates.forEach((recDate) => {
      const transportLotForDate = savedTransportLotList.filter(
        (lot) => lot.recDate && isEqualDate(lot.recDate, recDate)
      )

      if (transportLotForDate.length > 0) {
        transportLotForDate.forEach((transportLot) => {
          const stockItemPlan = stockListPlan?.find(
            (stockItem) => 
              isEqualDate(stockItem.recDate, recDate)
              && stockItem.itemCode === transportLot.itemCode
          )
          const orderPlan: OrderPlan = {
            ...initRecordEditEx(),
            orgCenterId: stockItemPlan?.orgCenterId || '',
            companyId: transportLot.companyId || 0,
            centerId: transportLot.centerId || 0,
            orgSupplierId: transportLot.orgSupplierId || '',
            recDate,
            orderDate: transportLot.orderDate,
            transportLotNo: transportLot.transportLotNo || null,
            itemCode: transportLot.itemCode || '',
            orgItemCode: transportLot.orgItemCode || '',
            itemName: stockItemPlan?.itemName || '',
            expectedRank: stockItemPlan?.expectedRank ?? null,
            isExpectedRankAlert: stockItemPlan?.isExpectedRankAlert ?? false,
            orderQuantityRec: stockItemPlan?.orderQuantityRec ?? null,
            lotQuantity: transportLot.lotQuantity || 0,
            maxPalletQuantity: transportLot.maxPalletQuantity || 0,
            lotQuantityPallet: calculateLotQuantityPallet(
              transportLot.lotQuantity || 0,
              transportLot.maxPalletQuantity || 0
            ),
            lotItemWeight: calculateLotItemWeight(transportLot.lotQuantity || 0, transportLot.weight || 1),
            stockDays: stockItemPlan?.stockDays ?? 0,
            stockDaysInfluence: (stockItemPlan?.stockDays ?? 0) - (stockItemPlan?.initialStockDays ?? 0),
            orderDatePrev: transportLot.orderDatePrev || null,
            remarks: transportLot.remarks || '',
            lotQuantityPrev: transportLot.lotQuantityPrev || null,
            transportLotWeight: transportLot.weight || 0,
            weight: transportLot.weight || 0,
            orderTransportTypeId: transportLot.orderTransportTypeId || null,
            isUpdatedArrival: stockItemPlan?.isUpdatedArrival ?? false,
            ...(() => {
              const transportType = transportTypeSearch?.list?.find(
                (type: any) => type.transportTypeId === transportLot.orderTransportTypeId
              )
              return {
                transportTypeName: transportType?.transportTypeName || '',
                maxWeight: transportType?.maxWeight || 0,
                maxPalletCount: transportType?.maxPalletCount || 0,
              }
            })(),
            minLoadCapacity: (transportLot.minLoadCapacity ?? 0) * 100,
            stockDaysSetting: stockItemPlan?.stockDaysSetting || 0,
            isOrderConfirmed: newDayList.find(
              (item) => isEqualDate(item.datevalue, recDate)
            )?.isOrderConfirmed ?? false,
            centerName: stockItemPlan?.centerName || '',
            supplierName: stockItemPlan?.supplierName || '',
          }
          orderPlanListData.push(orderPlan)
        })
      } else {
        stockListPlan
          .filter((stockItem) => stockItem.recDate && isEqualDate(stockItem.recDate, recDate))
          .forEach((stockItem) => {
            const orderPlan: OrderPlan = {
              ...initRecordEditEx(),
              orgCenterId: stockItem.orgCenterId || '',
              companyId: stockItem.companyId || 0,
              centerId: stockItem.centerId || 0,
              orgSupplierId: stockItem.orgSupplierId || '',
              recDate,
              orderDate: stockItem.orderDate,
              transportLotNo: null,
              itemCode: stockItem.itemCode || '',
              orgItemCode: stockItem.orgItemCode || '',
              itemName: stockItem.itemName || '',
              expectedRank: stockItem.expectedRank ?? null,
              isExpectedRankAlert: stockItem.isExpectedRankAlert ?? false,
              orderQuantityRec: stockItem.orderQuantityRec ?? null,
              lotQuantity: stockItem.arrivalQuantity || 0,
              maxPalletQuantity: stockItem.maxPalletQuantity || 0,
              lotQuantityPallet: calculateLotQuantityPallet(
                stockItem.arrivalQuantity || 0,
                stockItem.maxPalletQuantity || 0
              ),
              lotItemWeight: calculateLotItemWeight(stockItem.arrivalQuantity || 0, stockItem.weight || 1),
              stockDays: stockItem.stockDays ?? 0,
              stockDaysInfluence: (stockItem.stockDays ?? 0) - (stockItem.initialStockDays ?? 0),
              orderDatePrev: stockItem.orderDate || null,
              remarks: stockItem.remarks || '',
              lotQuantityPrev: stockItem.arrivalQuantity || null,
              transportLotWeight: stockItem.weight || 0,
              weight: stockItem.weight || 0,
              orderTransportTypeId: null,
              transportTypeName: '',
              maxWeight: 0,
              maxPalletCount: 0,
              minLoadCapacity,
              stockDaysSetting: stockItem.stockDaysSetting || 0,
              isOrderConfirmed: false,
              centerName: stockItem.centerName || '',
              supplierName: stockItem.supplierName || '',
              isUpdatedArrival: stockItem.isUpdatedArrival ?? false,
            }
            orderPlanListData.push(orderPlan)
          })
      }
    })
    const filteredOrderPlanListData = orderPlanListData.filter((item) => item.lotQuantity !== undefined && item.lotQuantity !== null && item.lotQuantity > 0)
    setOrderPlanList(filteredOrderPlanListData)
    
    // Initialize transportSummaryList from stockDataResponse (take 21 days: systemDate -7 to systemDate +13)
    const transportSummaryData: TransportSummaryData[] = []

    const allDates = new Set<string>()

    // Add dates from original stock data within the time range
    const recDatesWithTodayOrder = new Set<string>()
    stockDataResponse?.list?.forEach((stock) => {
      if (stock.recDate) {
        const stockDate = normalizeDate(stock.recDate)
        if (stockDate && stockDate >= startDate && stockDate <= endDate) {
          const dateKey = stockDate.toLocaleDateString('sv-SE')
          allDates.add(dateKey)
          if (isEqualDate(stock.orderDate, systemDate)) {
            recDatesWithTodayOrder.add(stock.recDate.toLocaleDateString('sv-SE'))
          }
        }
      }
    })
    
    // Add dates from transport lot data within the time range
    savedTransportLotList?.forEach((transportLot) => {
      if (transportLot.recDate) {
        const lotDate = normalizeDate(transportLot.recDate)
        if (lotDate && lotDate >= startDate && lotDate <= endDate) {
          const dateKey = lotDate.toLocaleDateString('sv-SE')
          allDates.add(dateKey)
        }
      }
    })

    // Calculate for each day (same logic as orderPlanList)
    Array.from(allDates).sort().forEach((dateKey) => {
      const recDate = normalizeDate(dateKey)

      const dayObj = daylist.find(
        (item) => item.datevalue && isEqualDate(item.datevalue, recDate)
      );
      const rectypeName = dayObj?.rectypeName ?? '';
    

      // Case 1: There is a transport lot saved for this day
      const transportLotForDate = savedTransportLotList.filter(
        (lot) => lot.recDate && isEqualDate(lot.recDate, recDate)
      )

      let totalWeight = 0
      let isOrderDateToday = false
      const uniqueTransportLots = new Set<number>()

      if (transportLotForDate.length > 0) {
        // Case 1: There is a transport lot -> calculate from transport lot
        transportLotForDate.forEach((transportLot) => {
          if (isEqualDate(transportLot.orderDate, systemDate)) {
            isOrderDateToday = true
          }
          // Only calculate when transportLotNo and valid data
          if (transportLot.transportLotNo && transportLot.lotQuantity && transportLot.weight) {
            const weight = calculateLotItemWeight(transportLot.lotQuantity || 0, transportLot.weight || 0)
            totalWeight += weight
            uniqueTransportLots.add(transportLot.transportLotNo)
          }
        })
      }

      const transportSummary: TransportSummaryData = {
        date: dateKey,
        totalWeight: Number.isNaN(totalWeight) ? 0 : Number(totalWeight.toFixed(3)),
        totalVehicles: uniqueTransportLots.size,
        rectypeName: mapRectypeNameToUI(rectypeName),
        isOrderDateToday,
      }
      transportSummaryData.push(transportSummary)
      transportSummaryData.forEach((transportSummaryItem) => {
        transportSummaryItem.isOrderDateToday = Array.from(recDatesWithTodayOrder).some((recDateToday) =>
          isEqualDate(normalizeDate(transportSummaryItem.date), recDateToday)
        )
      })
    })

    setTransportSummaryList(transportSummaryData)
    setLoadingState(false)
    setSearchFormLastUpdateDatetime(stockDataResponse.lastUpdateDatetime)
    setHasGridEdited(false)
    if (!isOptimizeFetching.current) {
      shouldRunTransportLot.current = true
    }
  }, [stockDataResponse, transportTypeSearch, loginUserInfo?.systemDate])

  // Compare calculated stock quantity with API stock quantity whenever stockDataResponse changes (only when fetching API stock, not when editing/optimizing)
  useEffect(() => {
    if (isLoading || isUpdatingStockCenter || suppressMismatchCompare.current) {
      setMismatchedStockItemCodes([])
      return
    }
    if (stockDataResponse?.list && stockDataResponse.list.length > 0 && centerSearch && stockDataResponse?.daylist) {
      // Calculate date range from optimizeRangeEndDate if available, otherwise use defaults
      const systemDate = normalizeDate(loginUserInfo?.systemDate ?? Date.now())
      if (systemDate) systemDate.setHours(0, 0, 0, 0)
      const mismatched = new Set<string>()
      const startDateTypeCode = 0
      let endDateTypeCode = 0
      if (optimizeRangeEndDate && systemDate) {
        endDateTypeCode = calculateDaysDifference(systemDate, optimizeRangeEndDate) + 7
      }
      const stockListToCompare = stockDataResponse.list

      handleStockCalculation(
        stockDataResponse.list,
        stockListToCompare,
        loginUserInfo,
        { calculateShipQuantityStockBase, calculateStockQuantity, calculateAvgShipQuantityStockBase, calculateStockDays },
        calcParams,
        false,
        false,
        {
          // Only compare when fetching API stock to detect calculation mismatches
          // Do NOT compare when editing or optimizing
          compareStockQuantityWithAPI: {
            daylist: stockDataResponse.daylist,
            startDateTypeCode,
            endDateTypeCode,
            mismatchedItemCodes: mismatched,
            logDetails: false,
          }
        }
      )

      setMismatchedStockItemCodes(Array.from(mismatched))
    } else {
      setMismatchedStockItemCodes([])
    }
  }, [stockDataResponse?.list, stockDataResponse?.daylist, optimizeRangeEndDate, centerSearch, loginUserInfo, calcParams, calculateShipQuantityStockBase, calculateStockQuantity, calculateAvgShipQuantityStockBase, calculateStockDays])

  // Event Handlers
  const onOrderPlanChange = useCallback((field: keyof OrderPlan, target: OrderPlan, oldItem: OrderPlan) => {
    // Set edited state when data changes
    const systemDate = normalizeDate(loginUserInfo?.systemDate)
    if (target.orderDate && isEqualDate(target.orderDate, systemDate)) {
      setHasGridEdited(true)
    }

    if (field === 'transportLotNo') {
      setOrderPlanList(prevOrderPlanList => {
      let updatedOrderPlanList: OrderPlan[] = []
      if (oldItem.transportLotNo) {
        // Find same item code and transport lot no and rec date
          const orderPlanSame = prevOrderPlanList?.find(
          (item) =>
            item.itemCode === target.itemCode &&
            item.transportLotNo === target.transportLotNo &&
            isEqualDate(item.recDate, target?.recDate)
        )

        // Remove same item code and transport lot no and rec date
        updatedOrderPlanList =
            prevOrderPlanList?.filter(
            (item) =>
              !(
                item.itemCode === target.itemCode &&
                item.transportLotNo === target.transportLotNo &&
                isEqualDate(item.recDate, target?.recDate)
              )
          ) ?? []

        // Update order plan list
        updatedOrderPlanList =
          updatedOrderPlanList?.map((orderPlan) => {
            const editedFields = new Set(orderPlan.editedFields || [])
            if (!editedFields.has(field)) {
              editedFields.add(field)
            }

            if (
              orderPlan.itemCode === target.itemCode &&
              orderPlan.id === target.id &&
              isEqualDate(orderPlan.recDate, target?.recDate)
            ) {
              if (orderPlanSame) {
                const newLotQuantity = (orderPlanSame.lotQuantity ?? 0) + (target.lotQuantity ?? 0)
                const mergedEditedFields = new Set<string>(
                  Array.from(orderPlanSame?.editedFields ?? []).concat(Array.from(editedFields))
                )
                return {
                  ...orderPlanSame,
                  lotQuantity: newLotQuantity,
                  lotQuantityPallet: calculateLotQuantityPallet(
                    newLotQuantity,
                    orderPlan.maxPalletQuantity || 0
                  ),
                  lotItemWeight: calculateLotItemWeight(newLotQuantity, orderPlan.weight || 1),
                  editedFields: mergedEditedFields,
                  isEdited: true,
                }
              }
              return {
                ...orderPlan,
                [field]: target[field],
                editedFields,
                isEdited: true,
              }
            }

            return orderPlan
          }) ?? []

          // After mapping, if orderPlanSame was found, re-add it to the list to preserve the merged record
          if (orderPlanSame) {
            updatedOrderPlanList.push({
              ...orderPlanSame,
              lotQuantity: (orderPlanSame.lotQuantity ?? 0),
              lotQuantityPallet: calculateLotQuantityPallet(
                (orderPlanSame.lotQuantity ?? 0),
                orderPlanSame.maxPalletQuantity || 0
              ),
              lotItemWeight: calculateLotItemWeight(
                (orderPlanSame.lotQuantity ?? 0),
                orderPlanSame.weight || 1
              ),
              editedFields: new Set<string>(
                Array.from(orderPlanSame?.editedFields ?? []).concat(field)
              ),
              isEdited: true,
            })
          }
      } else {
        updatedOrderPlanList =
            prevOrderPlanList?.map((orderPlan) => {
            if (
              orderPlan.itemCode === target.itemCode &&
              orderPlan.id === target.id &&
              isEqualDate(orderPlan.recDate, target?.recDate)
            ) {
              const editedFields = new Set(orderPlan.editedFields || [])
              if (!editedFields.has(field)) {
                editedFields.add(field)
              }
              return {
                ...orderPlan,
                [field]: target[field],
                editedFields,
                isEdited: true,
              }
            }
            return orderPlan
          }) ?? []
      }
        updateTransportSummaryList(updatedOrderPlanList ?? [])
        return updatedOrderPlanList
      })
    }

    if (field === 'lotQuantity') {
      setOrderPlanList(prevOrderPlanList => {
      let updatedStockList: Stock[] = []
      if(oldItem.transportLotNo) {
        const delta = (target[field] ?? 0) - (oldItem.lotQuantity ?? 0)
        updatedStockList = stockList?.map((stock: Stock) => {
          if (
            stock.itemCode === target.itemCode &&
            isEqualDate(stock.recDate, target?.recDate)
          ) {
            return {
              ...stock,
              arrivalQuantity: ((stock.arrivalQuantity ?? 0) + delta) as number,
              arrivalQuantityPlan: ((stock.arrivalQuantity ?? 0) + delta) as number,
            }
          }
          return stock
        }) ?? []
      } else {
        updatedStockList = stockList?.map((stock: Stock) => {
          if (
            stock.itemCode === target.itemCode &&
            isEqualDate(stock.recDate, target?.recDate)
          ) {
            const newVal = (target[field] ?? 0) as number
            return {
              ...stock,
              arrivalQuantity: newVal,
              arrivalQuantityPlan: newVal,
            }
          }
          return stock
        }) ?? []
      }

      const stockListPlanUpdate = handleStockCalculation(
        stockDataResponse?.list ?? [],
        updatedStockList,
        loginUserInfo,
        { calculateShipQuantityStockBase, calculateStockQuantity, calculateAvgShipQuantityStockBase, calculateStockDays },
        calcParams,
        false,
      )
      setStockList(stockListPlanUpdate)

      // Update stockDays for all items have same itemCode with target itemCode
        const allUpdatedOrderPlanList = prevOrderPlanList?.map((orderPlan) => {
        if(orderPlan.itemCode === target.itemCode) {
          const stockUpdate = stockListPlanUpdate.find(
            (stock) => stock.itemCode === orderPlan.itemCode && isEqualDate(stock.recDate, orderPlan.recDate)
          )
          const updatedStockDays = stockUpdate?.stockDays ?? 0
          const initialStockDays = stockUpdate?.initialStockDays ?? 0
          
          return {
            ...orderPlan,
            stockDays: updatedStockDays,
            stockDaysInfluence: updatedStockDays - initialStockDays
          }
        }
        return orderPlan
      })

      const stockUpdates = stockListPlanUpdate.filter(
        (item) => 
          item.itemCode === target.itemCode
          && isEqualDate(item.recDate, target.recDate)
      )

      const updatedOrderPlanList = allUpdatedOrderPlanList?.map((orderPlan) => {
        const findStockUpdate = stockUpdates.find(
          (stockUpdate) =>
            stockUpdate.itemCode === orderPlan.itemCode
            && isEqualDate(stockUpdate.recDate, orderPlan.recDate)
        )

        const editedFields = new Set(orderPlan.editedFields || []);
        if (!editedFields.has(field)) {
          editedFields.add(field);
        }

        if (findStockUpdate) {
          if(orderPlan.transportLotNo === target.transportLotNo) {
            return {
              ...orderPlan,
              stockDays: findStockUpdate.stockDays ?? 0,
              stockDaysInfluence: (findStockUpdate.stockDays ?? 0) - (findStockUpdate.initialStockDays ?? 0),
              lotQuantity: target.lotQuantity ?? 0,
              lotQuantityPallet: calculateLotQuantityPallet(
                target.lotQuantity || 0,
                findStockUpdate.maxPalletQuantity || 0
              ),
              lotItemWeight: calculateLotItemWeight(target.lotQuantity || 0, findStockUpdate.weight || 1),
              editedFields,
              isEdited: true
            }
          }
          
          return {
            ...orderPlan,
            stockDays: findStockUpdate.stockDays ?? 0,
            stockDaysInfluence: (findStockUpdate.stockDays ?? 0) - (findStockUpdate.initialStockDays ?? 0)
          }
        }
        return orderPlan
      })
        updateTransportSummaryList(updatedOrderPlanList ?? [])
        return updatedOrderPlanList ?? []
      })
    }
    
    if (field === 'remarks') {
      setOrderPlanList(prevOrderPlanList => {
        const updatedOrderPlanList = prevOrderPlanList?.map((orderPlan) => {
        if (
          orderPlan.itemCode === target.itemCode &&
          orderPlan.id === target.id &&
          isEqualDate(orderPlan.recDate, target?.recDate)
        ) {
          const editedFields = new Set(orderPlan.editedFields || []);
          if (!editedFields.has(field)) {
            editedFields.add(field);
          }

          return {
            ...orderPlan,
            [field]: target[field],
            editedFields,
            isEdited: true
          }
        }
        return orderPlan
      })
        return updatedOrderPlanList ?? []
      })
    }
  }, [stockDataResponse, loginUserInfo, setHasGridEdited, stockList])

  const StockQuantityListCalculation = useCallback((StockQuantityList: Stock[], responseStockList: Stock[], nextRecDate: Date) => {
      const stockQuantityList = StockQuantityList.filter(item => item.recDate && item.recDate <= nextRecDate)
      const responseStockQuantityList = responseStockList.filter(item => item.recDate && item.recDate > nextRecDate)

      const calculatedStockList = handleStockCalculation(
        responseStockList ?? [],
        [...stockQuantityList ?? [], ...responseStockQuantityList ?? []],
        loginUserInfo,
        { calculateShipQuantityStockBase, calculateStockQuantity, calculateAvgShipQuantityStockBase, calculateStockDays },
        calcParams,
        false,
        true
      )
      return calculatedStockList
  }, [loginUserInfo, stockDataResponse, stockList])

  const doSearch = useCallback(
    async (requestParam: GetStockStockGetRequest) => {
      setMismatchedStockItemCodes([])
      try {
        navigate(getSearchUrl(requestParam, transportTypeData as TransportTypeUrl[], minLoadCapacity));

        // Fetch with optimize range (2 API calls: get range, then get data)
        await fetchStockWithOptimizeRange(requestParam)
        
        if (
          requestParam.companyId === loginUserInfo!.companyId &&
          loginUserInfo?.tenantId &&
          loginUserInfo?.companyId
        ) {
          if (transportTypeData && Array.isArray(transportTypeData) && transportTypeData.length > 0) {
            const transportTypeObj = Object.fromEntries(
              transportTypeData.map((item: any, index: number) => [index, item?.transportTypeId])
            );
            setSearchParamState<OrderPlanTransportLotRequest>(
              loginUserInfo?.tenantId,
              loginUserInfo?.companyId,
              'OrderPlanTransportLot',
              {
                ...requestParam,
                transportType: transportTypeObj,
                viewUnitType: 'case',
                localMinLoadCapacity: minLoadCapacity,
                orgSupplierId: requestParam.orgSupplierId,
                }
              )
          } else {
            setSearchParamState<OrderPlanTransportLotRequest>(
              loginUserInfo?.tenantId,
              loginUserInfo?.companyId,
              'OrderPlanTransportLot',
              {
                ...requestParam,
                viewUnitType: 'case',
                localMinLoadCapacity: minLoadCapacity,
                orgSupplierId: requestParam.orgSupplierId,
                }
              )
          }
        }
        setSearchParam(requestParam)
        setHasGridEdited(false)
        shouldRunTransportLot.current = true
      } catch (error) {
        // Handle error silently
      }
    },
    [fetchStockWithOptimizeRange, loginUserInfo, setSearchParamState, setSearchParam, setHasGridEdited, minLoadCapacity]
  )

  const handleRegister = useCallback(async() => {
    const systemDate = normalizeDate(loginUserInfo?.systemDate ?? Date.now())!
    systemDate.setHours(0, 0, 0, 0)

    // Get update rec date if event click from search condition
    const updateRecDateList = new Set(stockList?.filter(item => isEqualDate(item.orderDate, systemDate)).map(item => item.recDate));
    const [lastUpdateRecDate] = Array.from(updateRecDateList)
      .filter((d): d is Date => d !== null)
      .sort((a, b) => b.getTime() - a.getTime());
    const updateRecDate  = lastUpdateRecDate;
    const nextRecDateIndex = orderPlanRecDates.findIndex(date => isEqualDate(date, updateRecDate))
    const nextRecDate = orderPlanRecDates[nextRecDateIndex + 1]

    // Validate transport lot data
    const allRecDate = orderPlanRecDates.filter(date => normalizeDate(date)! <= normalizeDate(updateRecDate)!)
    if (!validateTransportLotData(allRecDate)) {
      return Promise.resolve(false)
    }

    // Update handle
    return updateHandle(updateRecDate, nextRecDate, systemDate).then(() => true)
  }, [stockList, orderPlanList, validationFunction])

  const handleOrderRecommend = useCallback(async () => {
    const systemDate = normalizeDate(loginUserInfo?.systemDate ?? Date.now())!
    systemDate.setHours(0, 0, 0, 0)

    suppressMismatchCompare.current = true
    try {

    // 登録（反映）してから最適化を実行。登録に失敗したら最適化は実行しない
    let registered: boolean
    try {
      registered = await handleRegister()
    } catch {
      return
    }
    if (!registered) return

    if (!stockList || stockList.length === 0) {
      showResultMessage(setResultMessage, {
        status: 'warning',
        message: '対象データがありません。'
      })
      return
    }
    setMismatchedStockItemCodes([])
    // Fetch center supplier data with orgSupplierId to get correct data
    const responseCenterSupplier = await fetchCenterSupplierSearch({companyId: searchParam.companyId})
    
    const currentCenterSupplier = responseCenterSupplier?.data?.list?.find(
      (cs: MstCenterSupplier) => cs.companyId === searchParam.companyId && cs.centerId === searchParam.centerId && cs.orgSupplierId === searchParam.orgSupplierId
    )

    await fetchCenterSearch({ companyId: searchParam.companyId })
    const currentCenter = centerSearch?.list?.find(
      (c: MstCenter) => c.companyId === searchParam.companyId && c.centerId === searchParam.centerId
    )
    const shipPreviousStockFlg = !!currentCenter?.shipPreviousStockFlg
    
    if (!currentCenterSupplier) {
      showResultMessage(setResultMessage, {
        status: 'warning',
        message: '最新のマスタ情報が取得できません'
      })
      return
    }
    
    // Get optimizeRange from currentCenterSupplier, convert to number, fallback to 14
    const optimizeRange = currentCenterSupplier.optimizeRange ? Number(currentCenterSupplier.optimizeRange) : Number(defaultOptimizeRange)
    const optimizeLeadtime = currentCenterSupplier.trOrderLeadtime ? Number(currentCenterSupplier.trOrderLeadtime) : 0
    const optimizeStartDate = addDays(systemDate, -30)

    // fix:最適化期間の開始日(start_date)を画面表示中の在庫データから算出する。
    // start_date = 入荷日(is_arrival_day) かつ order_limit_date(=orderDate) >= operationDate を満たす最小の rec_date。
    // ds-model 側 build_optimization_period と同じ判定（API は order_limit_date を orderDate として返す）。当画面は単一 center-supplier。
    const operationDate = systemDate
    let optimizeStartRecDate: Date | null = null
    ;(stockDataResponse?.list ?? []).forEach((s) => {
      if (s.centerId !== searchParam.centerId || s.orgSupplierId !== searchParam.orgSupplierId) return
      if (s.isArrivalDay !== true) return
      const orderLimit = normalizeDate(s.orderDate)
      const rec = normalizeDate(s.recDate)
      if (!orderLimit || !rec) return
      if (orderLimit < operationDate) return
      if (optimizeStartRecDate === null || rec < optimizeStartRecDate) optimizeStartRecDate = rec
    })
    // 取得・対象範囲の終端 = start_date + optimizeRange + 7日(安全マージン)。
    // start_date を算出できない場合は従来式へフォールバックし、従来範囲を下回らないよう大きい方を採用。
    const fallbackEndDate = normalizeDate(addDays(systemDate, optimizeRange + optimizeLeadtime + 7))!
    const startBasedEndDate = optimizeStartRecDate ? normalizeDate(addDays(optimizeStartRecDate, optimizeRange + 7))! : fallbackEndDate
    let optimizeEndDate = startBasedEndDate > fallbackEndDate ? startBasedEndDate : fallbackEndDate
    const optimizeEndOffset = calculateDaysDifference(systemDate, optimizeEndDate)
    let responseStockOptimize: Stock[] = []
    let responseStockDayList: Daylist[] = []

    try {
      // Prevent automatic transport lot calculation after fetching stock data
      shouldRunTransportLot.current = false
      isOptimizeFetching.current = true
      const PastStockOptimize = await fetchStockData({
        ...searchParam,
        startDateTypeCode: String(-30),
        endDateTypeCode: String(optimizeEndOffset)
      }
      )
      responseStockOptimize = [...(PastStockOptimize?.list ?? [])]
      responseStockDayList = [...(PastStockOptimize?.daylist ?? [])]
      // Map stockList to StockList format for API
      const stockListForOptimize: OrderQuantityStockList[] = responseStockOptimize
        .filter((stock) => {
          const stockRecDate = normalizeDate(stock.recDate)
          return (
            stock.companyId &&
            stock.centerId &&
            stock.orgSupplierId &&
            stockRecDate &&
            stockRecDate >= optimizeStartDate &&
            stockRecDate <= optimizeEndDate
          )
        })
        .map((stock) => ({
          companyId: stock.companyId!,
          centerId: stock.centerId!,
          orgSupplierId: stock.orgSupplierId!,
          itemCode: stock.itemCode!,
          shipQuantity: stock.shipQuantity ?? 0,
          shipQuantityOptimizeBase: stock.shipQuantityOptimizeBase ?? 0,
          stockQuantity: stock.stockQuantity ?? 0,
          arrivalQuantity: stock.arrivalQuantity ?? 0,
          arrivalFixFlg: stock.arrivalFixFlg ?? false,
          recDate: stock.recDate ? convertDateToRequestParam(normalizeDate(stock.recDate)!) : null,
          orderDate: stock.orderDate ? convertDateToRequestParam(normalizeDate(stock.orderDate)!) : null,
          safetyStockSetting: stock.safetyStockSetting,
          stockDaysSetting: stock.stockDaysSetting,
          minStockQuantity: stock.minStockQuantity,
          caseLot: stock.caseLot ?? 0,
          minOrderQuantity: stock.minOrderQuantity ?? 1,
          orderUnitType: stock.orderUnitType ?? '',
          weight: stock.weight ?? 0,
          arrivalRatio: stock.arrivalRatio ?? 0,
          stopArrival: stock.stopArrival ?? false,
          avgShipQuantityStockBase: stock.avgShipQuantityStockBase ?? 0,
          isArrivalDay: stock.isArrivalDay ?? false,
        }))

      if (stockListForOptimize.length === 0) {
        showResultMessage(setResultMessage, {
          status: 'warning',
          message: '発注推奨数算出対象データがありません。'
        })
        onReload()
        return
      }

      // Call optimize API
      const response = await optimizeOrderQuantity({
        operationDate: convertDateToRequestParam(normalizeDate(systemDate)!)!,
        shipPreviousStockFlg,
        centerSupplierInfo: {
          optimizeType: currentCenterSupplier.optimizeType ?? 'weight',
          trMinOrderQuantityOnce: currentCenterSupplier.trMinOrderQuantityOnce ?? 0,
          trOrderLeadtime: currentCenterSupplier.trOrderLeadtime ?? 0,
        },
        stockList: stockListForOptimize,
        optimizeRange,
      })

      if (!response) {
        setLoadingState(false)
        showResultMessage(setResultMessage, {
          status: 'error',
          message: '発注推奨数算出が失敗しました'
        })
        onReload()
        return
      }

      showResultMessage(setResultMessage, {
        status: 'success',
        message: `発注推奨数算出が完了しました`
      })

      // Update stockList with orderQuantityRecommend from response
      setLoadingState(true)
      let updatedStockList: Stock[] = []
      let endUpdateDate: Date | null = null
      if (response && response.orderRecommendList.length > 0) {
        updatedStockList = responseStockOptimize.map((stock: Stock) => {
          const recommendation = response.orderRecommendList.find(
            (rec) =>
              rec.companyId === stock.companyId &&
              rec.centerId === stock.centerId &&
              rec.itemCode === stock.itemCode &&
              rec.orgSupplierId === stock.orgSupplierId &&
              isEqualDate(normalizeDate(rec.arrivalDate), stock.recDate)
          )

          if (recommendation) {
            if (stock.recDate) {
              const recDate = normalizeDate(stock.recDate)
              if (endUpdateDate === null || (recDate && recDate > endUpdateDate)) {
                endUpdateDate = recDate
              }
            }
            return {
              ...stock,
              arrivalQuantity: recommendation.orderQuantityRecommend,
              orderQuantityRecommend: recommendation.orderQuantityRecommend,
              orderRecommendCalcDatetime: normalizeDate(recommendation.orderRecommendCalcDatetime),
              orderQuantityLpResult: recommendation.orderQuantityLpResult ?? null,
              minStockForecastBased: recommendation.minStockForecastBased ?? null,
              minStockShortageBased: recommendation.minStockShortageBased ?? null,
              calcSafety: recommendation.calcSafety ?? null,
              isOrderQuantityFixed: stock.arrivalFixFlg,
              orderQuantity: stock.isUpdatedArrivalTmp ? null : stock.orderQuantity,
            }
          }
          return stock
        })
      } else {
        updatedStockList = responseStockOptimize.map((stock: Stock) => ({
            ...stock,
            orderQuantityRecommend: null,
            orderRecommendCalcDatetime: null,
            orderQuantityLpResult: null,
            minStockForecastBased: null,
            minStockShortageBased: null,
            calcSafety: null,
        } as Stock))
      }
      if (endUpdateDate) {
        optimizeEndDate = endUpdateDate
      }
      const updateDateList = stockDataResponse.daylist?.filter(item => {
        if (item.isOrderConfirmed === false && item.datevalue) {
          const itemDate = normalizeDate(item.datevalue)
          const sysDate = normalizeDate(systemDate)
          return itemDate && sysDate &&
            itemDate >= sysDate &&
            itemDate <= optimizeEndDate
        }
        return false
      }).map(item => item.datevalue)

      const stockListPlanUpdate = handleStockCalculation(
        responseStockOptimize,
        updatedStockList ?? [],
        loginUserInfo,
        { calculateShipQuantityStockBase, calculateStockQuantity, calculateAvgShipQuantityStockBase, calculateStockDays },
        calcParams,
        false,
        true,
      )
      handleUpdateStockCenter(stockListPlanUpdate, updateDateList as Date[])

    } catch (error: any) {
      showResultMessage(setResultMessage, {
        status: 'error',
        message: error.message || '発注推奨数算出処理中にエラーが発生しました。'
      })
      setLoadingState(false)
    } finally {
      isOptimizeFetching.current = false
    }
    } finally {
      suppressMismatchCompare.current = false
    }
  }, [handleRegister, stockList, searchParam, centerSupplierSearchResult, centerSupplierSearch, optimizeOrderQuantity, loginUserInfo, fetchStockData])


  const handleUpdateStockCenter = useCallback(async(stockDataList: Stock[], updateDateList: Date[] | null) => {
    setLoadingState(true)

    const stockCenterList = stockDataList.filter(item => {
      const stockRecDate = normalizeDate(item.recDate)
      return (
        item.companyId &&
        item.centerId &&
        item.orgSupplierId &&
        stockRecDate &&
        updateDateList?.some(date => isEqualDate(date, stockRecDate))
      )
    }
    ).map(item => ({
      companyId: item.companyId ?? 0,
      centerId: item.centerId ?? 0,
      itemCode: item.itemCode ?? '',
      orgSupplierId: item.orgSupplierId,
      recDate: item.recDate ? convertDateToRequestParam(normalizeDate(item.recDate)!) : null,
      stockQuantity: item.stockQuantity,
      stockDays: item.stockDays,
      orderQuantityRecommend: item.orderQuantityRecommend,
      orderRecommendCalcDatetime: item.orderRecommendCalcDatetime ? convertDateToRequestParam(normalizeDate(item.orderRecommendCalcDatetime)!) : null,
      orderQuantityLpResult: item.orderQuantityLpResult,
      minStockForecastBased: item.minStockForecastBased,
      minStockShortageBased: item.minStockShortageBased,
      calcSafety: item.calcSafety,
      shipQuantityStockBase: item.shipQuantityStockBase,
      avgShipQuantityStockBase: item.avgShipQuantityStockBase,
      isOrderQuantityFixed: item.isOrderQuantityFixed ?? null,
      stockQuantityRecMorning: item.stockQuantityRecMorning,
    }));

      const response = await fetchUpdateStockCenter({
        stockUpdate: {
          body: stockCenterList,
        }
      })
      showResultMessage(setResultMessage, {
        status: response.result.status as 'success' | 'warning' | 'error',
        message: response.result.message || '入出荷数の更新中にエラーが発生しました。'
      })
      onReload()
      setLoadingState(false)
  }, [fetchUpdateStockCenter])

  // Context menu handlers
  const onDoUpdateForRecDate = useCallback(async(recDate: Date) => {
    const systemDate = normalizeDate(loginUserInfo?.systemDate ?? Date.now())!
    systemDate.setHours(0, 0, 0, 0)

    const updateRecDate = normalizeDate(recDate)!

    // Get next rec date if event click from data list
    const nextRecDateIndex = orderPlanRecDates.findIndex(date => isEqualDate(date, updateRecDate))
    const nextRecDate = orderPlanRecDates[nextRecDateIndex + 1]


    // Validate transport lot data
    const allRecDate = orderPlanRecDates.filter(date => normalizeDate(date)! <= normalizeDate(updateRecDate)!)
    if (!validateTransportLotData(allRecDate)) {
      return
    }

    // Update handle
    updateHandle(updateRecDate, nextRecDate, systemDate)

  }, [stockList, orderPlanList, validationFunction])

  const updateHandle = useCallback(async (updateRecDate: Date, nextRecDate: Date, systemDate: Date, updateFlag = false) => {

    const stockPlanList = (stockList ?? [])
      .filter(item => 
        item.recDate
        && item.arrivalQuantity !== null
        && item.arrivalQuantity !== undefined
        && normalizeDate(updateRecDate)! >= normalizeDate(item.recDate)!
      )
      .map(item => {
        const stockPlanUpdateDatetime = item.stockPlanUpdateDatetime ? normalizeDate(item.stockPlanUpdateDatetime)! : null
        return {
          companyId: item.companyId ?? 0,
          centerId: item.centerId ?? 0,
          itemCode: item.itemCode ?? '',
          orgSupplierId: item.orgSupplierId ?? '',
          recDate: item.recDate ? convertDateToRequestParam(normalizeDate(item.recDate)!) : null,
          orderDate: item.orderDate ? convertDateToRequestParam(normalizeDate(item.orderDate)!) : null,
          shipQuantityPlan: null,
          arrivalQuantityPlan: (orderPlanList ?? [])
            .filter(plan => 
              isEqualDate(plan.recDate, item.recDate) && 
              plan.itemCode === item.itemCode
            )
            .reduce((sum, plan) => sum + (plan.lotQuantity ?? 0), 0),
          olditemShipStopFlg: null,
          remarks: item.remarks ?? '',
          stockPlanUpdateDatetime: stockPlanUpdateDatetime ? convertDateToRequestParam(stockPlanUpdateDatetime) : null,
          orderQuantityRecommend: item.orderQuantityRecommend ?? 0,
        }
      });

    const orderPlanTransportLotList = (orderPlanList ?? [])
      .filter(item => 
        item.recDate
        && normalizeDate(updateRecDate)! >= normalizeDate(item.recDate)!
        && item.orderTransportTypeId !== null
        && item.transportLotNo !== null
      )
      .map(item => ({
        companyId: item.companyId ?? 0,
        centerId: item.centerId ?? 0,
        orgSupplierId: item.orgSupplierId ?? '',
        recDate: item.recDate ? convertDateToRequestParam(normalizeDate(item.recDate)!) : null,
        transportLotNo: item.transportLotNo ?? 0,
        itemCode: item.itemCode ?? '',
        orgItemCode: item.orgItemCode ?? '',
        minLoadCapacity: item.minLoadCapacity ? ((item.minLoadCapacity ?? 0)/100) : (Number(minLoadCapacity) / 100),
        orderTransportTypeId: item.orderTransportTypeId ?? 0,
        orderDate: item.orderDate ? convertDateToRequestParam(normalizeDate(item.orderDate)!) : null,
        lotQuantity: item.lotQuantity ?? 0,
        weight: item.weight ?? 0,
        maxPalletQuantity: item.maxPalletQuantity ?? 0,
        orderDatePrev: item.orderDatePrev ? convertDateToRequestParam(normalizeDate(item.orderDatePrev)!) : null,
        lotQuantityPrev: item.lotQuantityPrev ?? 0,
        remarks: item.remarks ?? '',
      }));

    // Stop execution if no transport lots found)
    if(orderPlanTransportLotList.length === 0 && !updateFlag) {
      showResultMessage(setResultMessage, {
        status: 'error',
        message: '本日発注対象の入荷日がありません'
      })
      return; // Early return to prevent API call
    }

    const pendingOrderNextDay = (stockList ?? [])
      .filter(item => 
        item.recDate
        && item.arrivalQuantity !== null
        && item.arrivalQuantity !== undefined
        && isEqualDate(item.recDate,nextRecDate)
      )
      .map(item => {
        const itemRecDate = item.recDate ? normalizeDate(item.recDate) : null
        return {
          companyId: item.companyId ?? 0,
          centerId: item.centerId ?? 0,
          itemCode: item.itemCode ?? '',
          recDate: itemRecDate ? convertDateToRequestParam(itemRecDate) : null,
          orderRecommendCalcDatetime: item.orderRecommendCalcDatetime ? convertDateToRequestParam(item.orderRecommendCalcDatetime) : null,
          arrivalQuantityPlan: (orderPlanList ?? [])
            .filter(plan => 
              isEqualDate(plan.recDate, item.recDate) && 
              plan.itemCode === item.itemCode
            )
            .reduce((sum, plan) => sum + (plan.lotQuantity ?? 0), 0),
          orderQuantityRecommend: item.orderQuantityRecommend ?? null,
        }
      });
    
    let calculatedStockList: Stock[] = []

    if (nextRecDate) {
      calculatedStockList = StockQuantityListCalculation(stockList ?? [], stockDataResponse?.list ?? [], nextRecDate)
    } else {
      calculatedStockList = handleStockCalculation(
        stockDataResponse?.list ?? [],
        stockList ?? [],
        loginUserInfo,
        { calculateShipQuantityStockBase, calculateStockQuantity, calculateAvgShipQuantityStockBase, calculateStockDays },
        calcParams,
        false,
        true
      )
    }

    const stockListUpdate: StockUpdateBase[] = (calculatedStockList ?? [])
    .filter(item => 
      item.companyId &&
      item.centerId &&
      item.itemCode &&
      item.orgSupplierId &&
      item.recDate &&
      normalizeDate(item.recDate)! >= normalizeDate(systemDate)!
    )
    .map((item: Stock) => ({
      companyId: item.companyId ?? 0,
      centerId: item.centerId ?? 0,
      itemCode: item.itemCode ?? '',
      orgSupplierId: item.orgSupplierId,
      recDate: item.recDate ? convertDateToRequestParam(normalizeDate(item.recDate)!) : null,
      stockQuantity: item.stockQuantity,
      stockDays: item.stockDays,
      orderQuantityRecommend: item.orderQuantityRecommend,
      orderRecommendCalcDatetime: item.orderRecommendCalcDatetime ? convertDateToRequestParam(normalizeDate(item.orderRecommendCalcDatetime)!) : null,
      orderQuantityLpResult: item.orderQuantityLpResult,
      minStockForecastBased: item.minStockForecastBased,
      minStockShortageBased: item.minStockShortageBased,
      calcSafety: item.calcSafety,
      shipQuantityStockBase: item.shipQuantityStockBase,
      avgShipQuantityStockBase: item.avgShipQuantityStockBase,
      stockQuantityRecMorning: item.stockQuantityRecMorning,
    }))

    try {
      setLoadingState(true)
      const response = await fetchOrderPlanTransportLotUpdate({
        orderPlanTransportLotUpdate: { body: {
        stockPlanList,
        orderPlanTransportLotList: updateFlag ? [] : orderPlanTransportLotList,
        pendingOrderNextDay: updateFlag ? [] : pendingOrderNextDay,
        stockList: stockListUpdate,
        }},
      })

      setHasGridEdited(false)

      const { lastUpdateDatetime } = response;
      const updatedStockList = stockList?.map((item) => {
        const isUpdated = stockPlanList.some(
          (updated) =>
            item.orgSupplierId === updated.orgSupplierId &&
            item.itemCode === updated.itemCode &&
            item.centerId === updated.centerId &&
            item.companyId === updated.companyId &&
            isEqualDate(item.recDate, updated.recDate)
        );

        return isUpdated
          ? { ...item, stockPlanUpdateDatetime: normalizeDate(lastUpdateDatetime ?? '') }
          : item;
      });

      if (updatedStockList) {
        setStockList(updatedStockList);
      }

      // Reset isEdited flag and update isOrderConfirmed for items up to updateRecDate
      setOrderPlanList(prevOrderPlanList => 
        prevOrderPlanList?.map(item => {
          const isConfirmedDate = item.recDate ? (normalizeDate(item.recDate)! <= normalizeDate(updateRecDate)!) : false
          return {
            ...item,
            isEdited: false,
            editedFields: new Set<string>(),
            isOrderConfirmed: isConfirmedDate
          }
        }) ?? []
      )
      setSearchFormLastUpdateDatetime(lastUpdateDatetime)
      setDayList(dayList?.map((day: any) => {
        if (normalizeDate(updateRecDate)! >= normalizeDate(day.datevalue)!) {
          return {
            ...day,
            isOrderConfirmed: true,
            isUpdatedArrivalTmp: false
          }
        }
        if (isEqualDate(day.datevalue, nextRecDate)) {
          return {
            ...day,
            isOrderConfirmed: false,
            isUpdatedArrivalTmp: true
          }
        }
        return {
          ...day,
          isOrderConfirmed: false,
          isUpdatedArrivalTmp: false
        }
      }))
      setLoadingState(false)
      setMismatchedStockItemCodes([])
    } catch (err: any) {
      setLoadingState(false)
      // eslint-disable-next-line no-console
      console.error('err', err)
    }
  }, [stockList, orderPlanList, validationFunction])

  // Helper function to validate transport lot data
  const validateTransportLotData = (recDates: Date[]): boolean => {
    const invalidItem = orderPlanList?.find(
      item =>
        item.recDate &&
        recDates.some(date => isEqualDate(item.recDate, date)) &&
        (item.transportLotNo == null || item.maxPalletCount === 0 || item.maxWeight === 0)
    )

    if (invalidItem) {
      showResultMessage(setResultMessage, {
        status: 'error',
        message: '該当する車両割当が見つかりません'
      })
      return false
    }
    return true
  }

  const onOptimizeLoadFactor = useCallback(async() => {
    // Use SearchCondition's validation function
    if (!validationFunction || !validationFunction()) {
      return
    }

    // Check if optimize load factor logic is selected
    if (!optimizeLoadFactorLogic || optimizeLoadFactorLogic === 0) {
      showResultMessage(setResultMessage, {
        status: 'error',
        message: '積載率調整を選択してください'
      })
      return
    }

    const systemDate = normalizeDate(loginUserInfo?.systemDate)

    // Optimize all dates (from SearchCondition)
    const first2OrderDates = orderPlanOrderDates.slice(0, 2)
    if (!isEqualDate(first2OrderDates[0], systemDate)) {
      showResultMessage(setResultMessage, {
        status: 'error',
        message: '本日発注対象の入荷日がありません'
      })
      return
    }

    // Find last recDate for today's order
    const updateRecDateList = new Set(
      stockList?.filter(item => isEqualDate(item.orderDate, systemDate))
        .map(item => item.recDate)
    );
    const [lastUpdateRecDate] = Array.from(updateRecDateList)
      .filter((d): d is Date => d !== null)
      .sort((a, b) => b.getTime() - a.getTime());
    
    const optimizeRecDate = lastUpdateRecDate;
    const nextRecDateIndex = orderPlanRecDates.findIndex(date => isEqualDate(date, optimizeRecDate))
    const nextRecDate = orderPlanRecDates[nextRecDateIndex + 1]
    
    // Set range for all dates
    const startIndex = 0
    const endIndex = orderPlanRecDates.length


    // Validate transport lot data
    const allRecDates = orderPlanRecDates.filter(date => 
      normalizeDate(date)! <= normalizeDate(nextRecDate)!
    )
    if (!validateTransportLotData(allRecDates)) {
      return
    }

    optimizeLoadFactorHandle(startIndex, endIndex, false)
  }, [orderPlanOrderDates, orderPlanRecDates, orderPlanList, stockList, optimizeLoadFactorLogic, minLoadCapacity, centerSupplierInfo, optimizeTransportLot, loginUserInfo, transportTypeSearch, setHasGridEdited, validationFunction])

  const onOptimizeLoadFactorForRecDate = useCallback(async (recDate: Date) => {
    // Use SearchCondition's validation function
    if (!validationFunction || !validationFunction()) {
      return
    }
    
    if (isEqualDate(recDate, orderPlanRecDates[orderPlanRecDates.length - 1])) {
      showResultMessage(setResultMessage, {
        status: 'error',
        message: 'この日は積載率調整できません。'
      })
      return
    }
    
    // Check if optimize load factor logic is selected
    if (!optimizeLoadFactorLogic || optimizeLoadFactorLogic === 0) {
      showResultMessage(setResultMessage, {
        status: 'error',
        message: '積載率調整を選択してください'
      })
      return
    }

    // Determine optimize range based on parameters

      if (!recDate) return

      const recDateIndex = orderPlanRecDates.findIndex(date => isEqualDate(date, recDate))
      if (recDateIndex === -1) return

      // Find next date with different orderDate
      const nextRecDate = orderPlanRecDates[recDateIndex + 1]

      // Set range for specific dates
      const startIndex = recDateIndex
      const endIndex = recDateIndex + 1

    // Validate transport lot data
    const allRecDates = orderPlanRecDates.filter(date => 
      normalizeDate(date)! <= normalizeDate(nextRecDate)! &&
      normalizeDate(date)! >= normalizeDate(recDate)!
    )
    if (!validateTransportLotData(allRecDates)) {
      return
    }
    // optimize load factor handle
    optimizeLoadFactorHandle(startIndex, endIndex, true)
  }, [orderPlanOrderDates, orderPlanRecDates, orderPlanList, stockList, optimizeLoadFactorLogic, minLoadCapacity, centerSupplierInfo, optimizeTransportLot, loginUserInfo, transportTypeSearch, setHasGridEdited, validationFunction])

  const optimizeLoadFactorHandle = useCallback(async (startIndex: number, endIndex: number, eventClick: boolean) => {
    const systemDate = normalizeDate(loginUserInfo?.systemDate)

    setLoadingState(true)
    let orderPlanListUpdate: OrderPlan[] = []
    let stockListUpdate = stockList ?? []
    let nextOrderDateFlg = 0
    const allMovedItems: Array<{
      orderDate: Date;
      newOrderDate: Date;
      itemCode: string;
      moveQuantity: number;
    }> = []
    let minLoadCapacityStatus: any[] = []

    const api = await getDsModelAPI()
    if (!api) {
      throw new Error('認証エラーが発生しました。')
    }
    
    // Loop through the determined range
    for (let index = startIndex; index < endIndex; index += 1) {
      const nextRecDateInLoop = orderPlanRecDates[index + 1]
      
      if (nextRecDateInLoop) {
        // Use updated data from previous iterations
        const currentOrderPlanList = orderPlanListUpdate.length > 0 ? orderPlanListUpdate : orderPlanList
        
        const orderPlanListToday = currentOrderPlanList?.filter(
          (lot) =>
            lot.recDate &&
            isEqualDate(lot.recDate, orderPlanRecDates[index])
        );
        
        const orderPlanListNextDay = currentOrderPlanList?.filter(
          (lot) =>
            lot.recDate &&
            nextRecDateInLoop &&
            isEqualDate(lot.recDate, nextRecDateInLoop)
        );

        // Check if next day orderDate is in first 2 orderPlanOrderDates (for all dates case)
        if (!eventClick) {
          const nextDayOrderDate = orderPlanListNextDay?.[0]?.orderDate
          if (nextDayOrderDate) {
            const first2OrderDates = orderPlanOrderDates.slice(0, 2)
            if (!first2OrderDates.some((date: Date) => 
              isEqualDate(date, nextDayOrderDate)
            )) {
              break
            }
            if (nextOrderDateFlg === 1) {
              break
            }
            if (isEqualDate(nextDayOrderDate, first2OrderDates[1])) {
              nextOrderDateFlg = 1
            }
          }
        }

        const todayTransportLotList = groupDayOrdersByTransportLot(orderPlanListToday!);
        const nextdayTransportLotList = groupDayOrdersByTransportLot(orderPlanListNextDay!);
        
        const todayStockList = stockListUpdate
          .filter(
            (item) =>
              item.recDate &&
              item.arrivalQuantity &&
              item.arrivalQuantity > 0 &&
              isEqualDate(item.recDate, orderPlanRecDates[index])
          )
          .map((item) => ({
            recDate: convertDateToRequestParam(normalizeDate(item.recDate)!),
            itemCode: item.itemCode ?? '',
            maxPalletQuantity: item.maxPalletQuantity ?? 0,
            weight: item.weight ?? 0,
            caseLot: item.caseLot ?? 0,
            arrivalQuantityPlan: Number(item?.arrivalQuantityPlan) ?? 0,
            stockQuantity: item.stockQuantity ?? 0,
            avgShipExpected: item.avgShipExpected ?? 0,
            stockDays: item.stockDays ?? null,
            stopArrival: item.stopArrival ?? false,
            stockDaysSetting: item.stockDaysSetting ?? 0,
            stopShip: item.stopShip ?? false,
            isUpdatedArrival: item.isUpdatedArrival ?? false,
            orderQuantityRec: item.orderQuantityRec ?? null,
          }));

        const nextdayStockList = stockListUpdate
          .filter(
            (item) =>
              item.recDate &&
              item.arrivalQuantity &&
              item.arrivalQuantity > 0 &&
              isEqualDate(item.recDate, nextRecDateInLoop)
          )
          .map((item) => ({
            recDate: convertDateToRequestParam(normalizeDate(item.recDate)!),
            itemCode: item.itemCode ?? '',
            maxPalletQuantity: item.maxPalletQuantity ?? 0,
            weight: item.weight ?? 0,
            caseLot: item.caseLot ?? 0,
            arrivalQuantityPlan: Number(item?.arrivalQuantityPlan) ?? 0,
            stockQuantity: item.stockQuantity ?? 0,
            avgShipExpected: item.avgShipExpected ?? 0,
            stockDays: item.stockDays ?? null,
            stopArrival: item.stopArrival ?? false,
            stockDaysSetting: item.stockDaysSetting ?? 0,
            stopShip: item.stopShip ?? false,
            isUpdatedArrival: item.isUpdatedArrival ?? false,
            orderQuantityRec: item.orderQuantityRec ?? null,
          }));

        if (todayStockList.length > 0 && nextdayStockList.length > 0) {
          try {
            // eslint-disable-next-line no-await-in-loop
            const optimizeData = await optimizeTransportLot({
              logic: optimizeLoadFactorLogic,
              fixLoadCapacity: Number(minLoadCapacity),
              minLoadCapacity: Number(minLoadCapacity),
              minOrderQuantityOnce: centerSupplierInfo?.minOrderQuantityOnce ?? 0,
              todayTransportLotList,
              nextdayTransportLotList,
              todayStockList,
              nextdayStockList,
            }, api);

            // Update stock list after optimize
            stockListUpdate = updateStockListByTransportLot(stockListUpdate, optimizeData?.todayStockList ?? [])
            stockListUpdate = updateStockListByTransportLot(stockListUpdate, optimizeData?.nextdayStockList ?? [])
            const stockListPlan = handleStockCalculation(
              stockDataResponse?.list ?? [],
              stockListUpdate,
              loginUserInfo,
              { calculateShipQuantityStockBase, calculateStockQuantity, calculateAvgShipQuantityStockBase, calculateStockDays },
              calcParams,
              false,
            )
            stockListUpdate = stockListPlan;

            // Update order plan list after optimize
            let todayLots: any[] = [];
            if (optimizeData.todayTransportLotList.length === 0) {
              todayLots = (todayStockList ?? []).map(stock => ({
                itemCode: stock.itemCode,
                lotQuantity: 0,
                recDate: stock.recDate
              }));
            } else {
              todayLots = optimizeData?.todayTransportLotList ?? [];
            }
            orderPlanListUpdate = updateOrderPlanListByTransportLot(
              stockListPlan ?? [],
              currentOrderPlanList ?? [],
              todayLots,
              minLoadCapacity,
              transportTypeSearch
            )

            let nextdayLots: any[] = [];
            if (optimizeData.nextdayTransportLotList.length === 0) {
              nextdayLots = (nextdayStockList ?? []).map(stock => ({
                itemCode: stock.itemCode,
                lotQuantity: 0,
                recDate: stock.recDate
              }));
            } else {
              nextdayLots = optimizeData?.nextdayTransportLotList ?? [];
            }
            orderPlanListUpdate = updateOrderPlanListByTransportLot(
              stockListPlan ?? [],
              orderPlanListUpdate ?? [],
              nextdayLots,
              minLoadCapacity,
              transportTypeSearch
            )

            // Collect moved items for later marking
            if (optimizeData?.movedItems) {
              allMovedItems.push(...optimizeData.movedItems)
            }

            if (optimizeData?.movedItems.length > 0) {
              const hasTodayOrderDate = optimizeData.movedItems.some(
                (movedItem) => 
                  (movedItem.orderDate && isEqualDate(movedItem.orderDate, systemDate)) ||
                  (movedItem.newOrderDate && isEqualDate(movedItem.newOrderDate, systemDate))
              )
              if (hasTodayOrderDate) {
                setHasGridEdited(true)
              }
            }
  
            // Update minLoadCapacityStatus based on logic
            minLoadCapacityStatus = [...minLoadCapacityStatus, ...optimizeData?.todayTransportLotList ?? []]

          } catch (err: any) {
            // eslint-disable-next-line no-console
            console.error('err', err)
          }
        }
      }
    }
    
    // Mark all moved items at the end to preserve flags across iterations
    if (allMovedItems.length > 0) {
      orderPlanListUpdate = updateOrderPlanListWithMovedItems(
        orderPlanListUpdate,
        allMovedItems
      )
      updateMinLoadCapacityStatus(minLoadCapacityStatus)
      // Update state once at the end
      setOrderPlanList(orderPlanListUpdate)
      updateTransportSummaryList(orderPlanListUpdate)
      setStockList(stockListUpdate)
    }
    else {
      showResultMessage(setResultMessage, {
        status: 'warning',
        message: '調整可能な商品がありません。'
      })
    }
    setLoadingState(false)
  }, [orderPlanOrderDates, orderPlanRecDates, orderPlanList, stockList, optimizeLoadFactorLogic, minLoadCapacity, centerSupplierInfo, optimizeTransportLot, loginUserInfo, transportTypeSearch, setHasGridEdited, validationFunction])

  const onTransportTypeChange = useCallback((recDate: Date, transportLotNo: number) => {
    // Set edited state when data changes
    const systemDate = normalizeDate(loginUserInfo?.systemDate)
    
    // Find the items for this transport lot
    const targetItems = orderPlanList?.filter((item) => 
      item.recDate && 
      item.transportLotNo === transportLotNo &&
      isEqualDate(item.recDate, recDate)
    )
    if (isEqualDate(systemDate, targetItems?.[0]?.orderDate)) {
      setHasGridEdited(true)
    }
    if (!targetItems || targetItems.length === 0) {
      showResultMessage(setResultMessage, {
        status: 'error',
        message: '該当する輸送ロットが見つかりません'
      })
      return
    }

    const currentTransportTypeId = targetItems[0]?.orderTransportTypeId
    setTransportTypeDialogOpen(true)
    setSelectedRecDate(recDate)
    setSelectedTransportLotNo(transportLotNo)
    setSelectedTransportTypeId(currentTransportTypeId)
  }, [orderPlanList, setHasGridEdited])

  const onChangeArrivalDate = useCallback((
    selectedData: Array<{recDate: Date, transportLotNo: number, itemCode: string}>,
    direction: 'next' | 'prev' ) => {
    const systemDate = normalizeDate(loginUserInfo?.systemDate)
    if (!orderPlanList || !stockList || !orderPlanRecDates) {
      showResultMessage(setResultMessage, {
        status: 'error',
        message: 'データが見つかりません'
      })
      return
    }
    let changeFlag = 0
    let lastTargetDate: Date | null = null
    let currentOrderPlanList = [...orderPlanList]
    let currentStockList = [...stockList]
    
    for (let i = 0; i < selectedData.length; i += 1) {
      const onChangeItem = selectedData[i]
      const { recDate, transportLotNo, itemCode } = onChangeItem

      // Find current order plan item using currentOrderPlanList
      const currentItem = currentOrderPlanList.find((item) =>
        item.recDate &&
        isEqualDate(item.recDate, recDate) &&
        item.itemCode === itemCode &&
        (transportLotNo ? item.transportLotNo === transportLotNo : item.transportLotNo === null || item.transportLotNo === undefined || item.transportLotNo === 0)
      )
      if (isEqualDate(systemDate, currentItem?.orderDate)) {
        setHasGridEdited(true)
      }

      if (!currentItem || !currentItem.lotQuantity || currentItem.lotQuantity === 0) {
        showResultMessage(setResultMessage, {
          status: 'error',
          message: '変更対象のアイテムまたは入荷数が見つかりません'
        })
        return
      }

      // Find target date based on direction
      const currentDateIndex = orderPlanRecDates.findIndex(date => 
        isEqualDate(date, recDate)
      )
      
      let targetDate: Date
      if (direction === 'next') {
        if (currentDateIndex === -1 || currentDateIndex >= orderPlanRecDates.length - 1) {
          showResultMessage(setResultMessage, {
            status: 'error',
            message: '次の日が見つかりません'
          })
          return
        }
        targetDate = orderPlanRecDates[currentDateIndex + 1]
      } else {
        if (currentDateIndex === -1 || currentDateIndex <= 0) {
          showResultMessage(setResultMessage, {
            status: 'error',
            message: '前の日が見つかりません'
          })
          return
        }
        targetDate = orderPlanRecDates[currentDateIndex - 1]
      }

      const arrivalQuantityToMove = currentItem.lotQuantity
      const targetOrderDate = currentOrderPlanList.find((item) =>
        isEqualDate(item.recDate, targetDate)
      )?.orderDate
      if (isEqualDate(targetOrderDate, systemDate)) {
        setHasGridEdited(true)
      }

      try {
        setLoadingState(true)

        // Update stock list if itemCode exists for current date using currentStockList
        const updatedStockList = currentStockList.map((stock) => {
          if (stock.itemCode === itemCode) {
            if (isEqualDate(stock.recDate, recDate)) {
              // Set current date arrival to 0
              return {
                ...stock,
                arrivalQuantity: (stock.arrivalQuantity || 0) - arrivalQuantityToMove,
                arrivalQuantityPlan: (Number(stock.arrivalQuantityPlan) || 0) - arrivalQuantityToMove,
              }
            }
            if (isEqualDate(stock.recDate, targetDate)) {
              // Add to target date
              return {
                ...stock,
                arrivalQuantity: (stock.arrivalQuantity || 0) + arrivalQuantityToMove,
                arrivalQuantityPlan: (Number(stock.arrivalQuantityPlan) || 0) + arrivalQuantityToMove,
              }
            }
          }
          return stock
        })

        // Check if itemCode exists for target date, if not create new stock item
        const hasTargetDateStock = updatedStockList.some((stock) =>
          stock.itemCode === itemCode && isEqualDate(stock.recDate, targetDate)
        )

        if (!hasTargetDateStock) {
          // Find original stock item for current date to copy base data
          const currentStockItem = currentStockList.find((stock) =>
            stock.itemCode === itemCode && isEqualDate(stock.recDate, recDate)
          )

          if (currentStockItem) {
            // Create new stock item for target date based on current stock item
            const newStockItem: OrderPlanStock = {
              ...currentStockItem,
              ...initRecordEditEx(true),
              recDate: targetDate,
              orderDate: targetOrderDate || null,
              arrivalQuantity: arrivalQuantityToMove,
              arrivalQuantityPlan: arrivalQuantityToMove,
              stockQuantity: currentStockItem.stockQuantity || 0,
              shipQuantity: currentStockItem.shipQuantity || 0,
              stockQuantityRecMorning: currentStockItem.stockQuantityRecMorning || 0,
              dateType: 'future' as any,
              avgShipExpected: currentStockItem.avgShipExpected || 0,
            }
            updatedStockList.push(newStockItem)
          }
        }

        // Recalculate stock with updated arrival quantities
        const stockListPlanUpdate = handleStockCalculation(
          stockDataResponse?.list ?? [],
          updatedStockList,
          loginUserInfo,
          { calculateShipQuantityStockBase, calculateStockQuantity, calculateAvgShipQuantityStockBase, calculateStockDays },
          calcParams,
          false,
        )

        // Find the target transport lot for target date using currentOrderPlanList
        const targetDateItems = currentOrderPlanList.filter((item) =>
          item.itemCode === itemCode && isEqualDate(item.recDate, targetDate)
        )

        // Check multiple transport lots have the same itemCode
        let targetTransportLotNo: number | null = null
        if (targetDateItems.length > 1) {
          // Take max transportLotNo
          const maxTransportLotNo = Math.max(...targetDateItems.map(item => item.transportLotNo || 0))
          targetTransportLotNo = maxTransportLotNo
        }

        // Update order plan list using currentOrderPlanList
        const updatedOrderPlanList = currentOrderPlanList.map((orderPlan) => {
          if (orderPlan.itemCode === itemCode) {
            if (isEqualDate(orderPlan.recDate, recDate) && 
                (transportLotNo ? orderPlan.transportLotNo === transportLotNo : orderPlan.transportLotNo === null || orderPlan.transportLotNo === undefined || orderPlan.transportLotNo === 0)) {
              // Set current item lot quantity to 0
              const editedFields = new Set(orderPlan.editedFields || [])
              editedFields.add('lotQuantity')
              
              // Find updated stock item to get new stockDays
              const updatedStockItem = stockListPlanUpdate.find((stock) =>
                stock.itemCode === itemCode && isEqualDate(stock.recDate, recDate)
              )
              
              return {
                ...orderPlan,
                lotQuantity: 0,
                lotQuantityPallet: 0,
                lotItemWeight: 0,
                stockDays: updatedStockItem?.stockDays ?? orderPlan.stockDays,
                stockDaysInfluence: (updatedStockItem?.stockDays ?? orderPlan.stockDays ?? 0) - (updatedStockItem?.initialStockDays ?? 0),
                editedFields,
                isEdited: true,
                isOrderDateChange: true
              }
            }
            if (isEqualDate(orderPlan.recDate, targetDate) && (targetTransportLotNo === null || orderPlan.transportLotNo === targetTransportLotNo)) {
              // Add to target date (only to the specific transport lot with max transportLotNo, or any if targetTransportLotNo is null)
              const editedFields = new Set(orderPlan.editedFields || [])
              editedFields.add('lotQuantity')
              
              // Find updated stock item to get new stockDays
              const updatedStockItem = stockListPlanUpdate.find((stock) =>
                stock.itemCode === itemCode && isEqualDate(stock.recDate, targetDate)
              )
              
              return {
                ...orderPlan,
                lotQuantity: (orderPlan.lotQuantity || 0) + arrivalQuantityToMove,
                lotQuantityPallet: calculateLotQuantityPallet(
                  (orderPlan.lotQuantity || 0) + arrivalQuantityToMove,
                  orderPlan.maxPalletQuantity || 0
                ),
                lotItemWeight: calculateLotItemWeight(
                  (orderPlan.lotQuantity || 0) + arrivalQuantityToMove,
                  orderPlan.weight || 1
                ),
                stockDays: updatedStockItem?.stockDays ?? orderPlan.stockDays,
                stockDaysInfluence: (updatedStockItem?.stockDays ?? orderPlan.stockDays ?? 0) - (updatedStockItem?.initialStockDays ?? 0),
                editedFields,
                isEdited: true,
                isOrderDateChange: true
              }
            }
          }
          return orderPlan
        })

        // If no existing order plan for target date, create new one
        const hasTargetDateOrderPlan = updatedOrderPlanList.some((item) =>
          isEqualDate(item.recDate, targetDate) &&
          item.itemCode === itemCode
        )

        if (!hasTargetDateOrderPlan) {
          const targetDateStockItem = stockListPlanUpdate.find((stock) =>
            isEqualDate(stock.recDate, targetDate) &&
            stock.itemCode === itemCode
          )

          if (targetDateStockItem) {
            // Check if target day already has transport lots allocated
            const targetDateOrderPlans = updatedOrderPlanList.filter((item) =>
              isEqualDate(item.recDate, targetDate) &&
              item.transportLotNo !== null && item.transportLotNo !== undefined && item.transportLotNo !== 0
            )
            
            // Determine transport lot assignment
            let assignedTransportLotNo: number | null = null
            let transportTypeInfo = {
              orderTransportTypeId: null as number | null,
              transportTypeName: '',
              maxWeight: 0,
              maxPalletCount: 0,
            }

            // If target day already has transport lots, assign to the last one
            if (targetDateOrderPlans.length > 0) {
              const lastTransportLotNo = Math.max(...targetDateOrderPlans.map(item => item.transportLotNo || 0))
              assignedTransportLotNo = lastTransportLotNo
              
              // Get transport type info from the last transport lot
              const lastTransportLotItem = targetDateOrderPlans.find(item => item.transportLotNo === lastTransportLotNo)
              if (lastTransportLotItem) {
                transportTypeInfo = {
                  orderTransportTypeId: lastTransportLotItem.orderTransportTypeId,
                  transportTypeName: lastTransportLotItem.transportTypeName || '',
                  maxWeight: lastTransportLotItem.maxWeight || 0,
                  maxPalletCount: lastTransportLotItem.maxPalletCount || 0,
                }
              }
            }

            const newOrderPlan: OrderPlan = {
              ...initRecordEditEx(),
              companyId: targetDateStockItem.companyId || 0,
              centerId: targetDateStockItem.centerId || 0,
              orgSupplierId: targetDateStockItem.orgSupplierId || '',
              recDate: targetDate,
              orderDate: targetDateStockItem.orderDate,
              transportLotNo: assignedTransportLotNo,
              itemCode: targetDateStockItem.itemCode || '',
              orgItemCode: targetDateStockItem.orgItemCode || '',
              itemName: targetDateStockItem.itemName || '',
              expectedRank: targetDateStockItem.expectedRank ?? null,
              isExpectedRankAlert: targetDateStockItem.isExpectedRankAlert ?? false,
              isUpdatedArrival: targetDateStockItem.isUpdatedArrival ?? false,
              orderQuantityRec: targetDateStockItem.orderQuantityRec ?? null,
              lotQuantity: arrivalQuantityToMove,
              maxPalletQuantity: targetDateStockItem.maxPalletQuantity || 0,
              lotQuantityPallet: calculateLotQuantityPallet(
                arrivalQuantityToMove,
                targetDateStockItem.maxPalletQuantity || 0
              ),
              lotItemWeight: calculateLotItemWeight(arrivalQuantityToMove, targetDateStockItem.weight || 1),
              stockDays: targetDateStockItem.stockDays ?? 0,
              stockDaysInfluence: (targetDateStockItem.stockDays ?? 0) - (targetDateStockItem.initialStockDays ?? 0),
              orderDatePrev: targetDateStockItem.orderDate || null,
              remarks: targetDateStockItem.remarks || '',
              lotQuantityPrev: arrivalQuantityToMove,
              transportLotWeight: targetDateStockItem.weight || 0,
              weight: targetDateStockItem.weight || 0,
              ...transportTypeInfo,
              minLoadCapacity,
              stockDaysSetting: targetDateStockItem.stockDaysSetting || 0,
              editedFields: new Set(['lotQuantity']),
              isEdited: true,
              isOrderDateChange: true,
              orgCenterId: targetDateStockItem?.orgCenterId || '',
            }
            updatedOrderPlanList.push(newOrderPlan)
          }
        }

        // Update temporary variables for next iteration
        currentOrderPlanList = updatedOrderPlanList
        currentStockList = stockListPlanUpdate
        changeFlag += 1
        if (i === selectedData.length - 1) {
          lastTargetDate = targetDate
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(`Error changing arrival to ${direction} day:`, error)
        showResultMessage(setResultMessage, {
          status: 'error',
          message: '入荷日変更中にエラーが発生しました'
        })
        setLoadingState(false)
        return
      }
    }
    
    // After processing all items, update state
    setOrderPlanList(currentOrderPlanList)
    setStockList(currentStockList)
    updateTransportSummaryList(currentOrderPlanList)
    setLoadingState(false)
    
    const directionText = direction === 'next' ? '次の日' : '前の日'
    if (changeFlag === 1) {
      showResultMessage(setResultMessage, {
        status: 'success',
        message: `${selectedData[0].itemCode}の入荷を${selectedData[0].recDate.toLocaleDateString('ja-JP')}から${normalizeDate(lastTargetDate)?.toLocaleDateString('ja-JP') ?? ''}に変更しました`
      })
    } else {
      showResultMessage(setResultMessage, {
        status: 'success',
        message: `${changeFlag}件を${selectedData[0].recDate.toLocaleDateString('ja-JP')}から${normalizeDate(lastTargetDate)?.toLocaleDateString('ja-JP') ?? ''}に変更しました`
      })
    }
  }, [orderPlanList, stockList, orderPlanRecDates, loginUserInfo, minLoadCapacity, setHasGridEdited])

  const onChangeArrivalNextDay = (selectedData: Array<{recDate: Date, transportLotNo: number, itemCode: string}>) => {
    onChangeArrivalDate(selectedData, 'next')
  }

  const onChangeArrivalPrevDay = (selectedData: Array<{recDate: Date, transportLotNo: number, itemCode: string}>) => {
    onChangeArrivalDate(selectedData, 'prev')
  }

  const onReload = useCallback(() => {
    setSearchTrigger((prev) => !prev)
    setHasGridEdited(false)
    setResultMessage(undefined)
  }, [setHasGridEdited])

  const onTransportLot = useCallback(async (showValidationErrors = true) => {
    if (!stockList || stockList?.length === 0 || !orderPlanList || orderPlanList?.length === 0 || !orderPlanRecDates || orderPlanRecDates?.length === 0) return
    // Get current systemDate
    const systemDate = normalizeDate(loginUserInfo?.systemDate)

    // Use SearchCondition's validation function
    if (!validationFunction || !validationFunction(showValidationErrors)) {
      // Validation failed - errors will be shown inline in SearchCondition
      return
    }

    navigate(getSearchUrl(searchParam, transportTypeData as TransportTypeUrl[], minLoadCapacity));

    setSearchParamState(
      loginUserInfo?.tenantId ?? '',
      loginUserInfo?.companyId ?? 0,
      'OrderPlanTransportLot',
      {
        ...searchParam,
        transportType: Array.isArray(transportTypeData)
          ? Object.fromEntries(transportTypeData.map((item, idx) => [idx, item.transportTypeId]))
          : {0: transportTypeData.transportTypeId},
        localMinLoadCapacity: minLoadCapacity,
      }
    )

    const groupStockList = stockList.filter((stock) => {
      const isConfirmed = dayList.find(day => {
        if (isEqualDate(day.datevalue, stock.recDate) && 
        day.isOrderConfirmed === false && 
        isEqualDate(day.orderDate, systemDate) &&
        stock.arrivalQuantity &&
        stock.arrivalQuantity > 0
      ) {
        setHasGridEdited(true)
      }
        return isEqualDate(day.datevalue, stock.recDate) &&
        day.isOrderConfirmed === false
      })
      return isConfirmed
    })

    const orderPlanListFiltered = orderPlanList.filter((stock) => {
      const isConfirmed = dayList.find(day =>
        isEqualDate(day.datevalue, stock.recDate) &&
        day.isOrderConfirmed === true)
      return isConfirmed
    })

    transportLotHandle(groupStockList, orderPlanListFiltered, false, systemDate!)
    
  }, [stockList, orderPlanRecDates, stockDataResponse, transportTypeSearch, minLoadCapacity, transportTypeData, calculateTransportLot, setHasGridEdited, validationFunction])

  const onTransportLotForRecDate = useCallback(async(recDate: Date) => {
    const isRecDate = normalizeDate(recDate)!
    if (!stockList || stockList?.length === 0 || !orderPlanList || orderPlanList?.length === 0 || !orderPlanRecDates || orderPlanRecDates?.length === 0) return
    // Get current systemDate
    const systemDate = normalizeDate(loginUserInfo?.systemDate)

    // Use SearchCondition's validation function
    if (!validationFunction || !validationFunction()) {
      // Validation failed - errors will be shown inline in SearchCondition
      return
    }

    navigate(getSearchUrl(searchParam, transportTypeData as TransportTypeUrl[], minLoadCapacity));

    setSearchParamState(
      loginUserInfo?.tenantId ?? '',
      loginUserInfo?.companyId ?? 0,
      'OrderPlanTransportLot',
      {
        ...searchParam,
        transportType: Array.isArray(transportTypeData)
          ? Object.fromEntries(transportTypeData.map((item, idx) => [idx, item.transportTypeId]))
          : {0: transportTypeData.transportTypeId},
        localMinLoadCapacity: minLoadCapacity,
      }
    )

    let groupStockList = [...stockList]
    let orderPlanListFiltered : any[] = []

    groupStockList = stockList.filter((stock) =>
      isEqualDate(stock.recDate, isRecDate) && stock.arrivalQuantity && stock.arrivalQuantity > 0
    )
    const hasTodayOrderDate = groupStockList.some(
      (stock) => stock.orderDate && isEqualDate(stock.orderDate, systemDate)
    )
    if (hasTodayOrderDate) {
      setHasGridEdited(true)
    }

    if (groupStockList.length === 0) {
      showResultMessage(setResultMessage, {
        status: 'error',
        message: '該当日のストックデータが見つかりません'
      })
      setLoadingState(false)
      return
    }
    // Delete - Remove existing order plan items for this recDate
    orderPlanListFiltered = orderPlanList?.filter((item) =>
      !isEqualDate(item.recDate, isRecDate)
    ) || []
    transportLotHandle(groupStockList, orderPlanListFiltered, true, isRecDate)

  }, [stockList, orderPlanRecDates, stockDataResponse, transportTypeSearch, minLoadCapacity, transportTypeData, calculateTransportLot, setHasGridEdited])

  const transportLotHandle = useCallback(async(groupStockList: any[], orderPlanListFiltered: any[], eventClick: boolean, isRecDate: Date) => {

    try {

      const groupedLots = groupStockDataByRecDate(
        groupStockList.filter(
          (stock) =>
            stock.recDate &&
            orderPlanRecDates.some(
              (date) => isEqualDate(stock.recDate, date)
            )
        ),
        minLoadCapacity,
        transportTypeData
      )

      if (groupedLots.length === 0) {
        showResultMessage(setResultMessage, {
          status: 'warning',
          message: '輸送ロットの計算に必要なデータが見つかりません。'
        })
        return
      }

      setLoadingState(true)

      const api = await getDsModelAPI()
      if (!api) {
        throw new Error('認証エラーが発生しました。')
      }
      const promises = groupedLots.map((lot) => calculateTransportLot(lot, api))
      const calculationResults = await Promise.all(promises)
      const allTransportLots = calculationResults.flatMap((result) => result.transportLotList || [])
      const newOrderPlanList: OrderPlan[] = []

      orderPlanRecDates.forEach((recDate) => {
        const transportLotsForDate = allTransportLots.filter(
          (lot) => lot.recDate && isEqualDate(lot.recDate, recDate)
        )

        if (transportLotsForDate.length > 0) {
          transportLotsForDate.forEach((transportLot) => {
            const stockItem = stockList?.find(
              (stock) =>
                stock.recDate &&
                isEqualDate(stock.recDate, recDate) &&
                stock.itemCode === transportLot.itemCode
            )

            const orderPlan: OrderPlan = {
              ...initRecordEditEx(),
              companyId: stockItem?.companyId || 0,
              centerId: stockItem?.centerId || 0,
              orgSupplierId: stockItem?.orgSupplierId || '',
              recDate,
              orderDate: stockItem?.orderDate || null,
              transportLotNo: transportLot.transportLotNo,
              itemCode: transportLot.itemCode,
              orgItemCode: stockItem?.orgItemCode || '',
              itemName: stockItem?.itemName || '',
              expectedRank: stockItem?.expectedRank ?? null,
              isExpectedRankAlert: stockItem?.isExpectedRankAlert ?? false,
              isUpdatedArrival: stockItem?.isUpdatedArrival ?? false,
              orderQuantityRec: stockItem?.orderQuantityRec ?? null,
              lotQuantity: transportLot.lotQuantity,
              maxPalletQuantity: stockItem?.maxPalletQuantity || 0,
              lotQuantityPallet: calculateLotQuantityPallet(
                transportLot.lotQuantity,
                stockItem?.maxPalletQuantity || 0
              ),
              lotItemWeight: calculateLotItemWeight(transportLot.lotQuantity, stockItem?.weight || 1),
              stockDays:
                stockList?.find(
                  (stock) =>
                    stock.recDate &&
                    isEqualDate(stock.recDate, recDate) &&
                    stock.itemCode === stockItem?.itemCode
                )?.stockDays ?? 0,
              stockDaysInfluence: (stockItem?.stockDays ?? 0) - (stockItem?.initialStockDays ?? 0),
              orderDatePrev: stockItem?.orderDate || null,
              remarks: stockItem?.remarks || '',
              lotQuantityPrev: stockItem?.arrivalQuantity || null,
              transportLotWeight: stockItem?.weight || 0,
              weight: stockItem?.weight || 0,
              orderTransportTypeId: Number(transportLot.transportTypeId),
              ...(() => {
                const transportType = transportTypeSearch?.list?.find(
                  (type: any) => type.transportTypeId === Number(transportLot.transportTypeId)
                )
                return {
                  transportTypeName: transportType?.transportTypeName || '',
                  maxWeight: transportType?.maxWeight || 0,
                  maxPalletCount: transportType?.maxPalletCount || 0,
                  centerName: stockItem?.centerName || '',
                  supplierName: stockItem?.supplierName || '',
                }
              })(),
              minLoadCapacity,
              stockDaysSetting: stockList?.find(
                (Item) => 
                  Item.recDate && 
                  isEqualDate(Item.recDate, recDate) && 
                  Item.itemCode === transportLot.itemCode
              )?.stockDaysSetting || 0,
              isOrderConfirmed: stockList?.find(
                (Item) => 
                  Item.recDate && 
                  isEqualDate(Item.recDate, recDate) && 
                  Item.itemCode === transportLot.itemCode
              )?.isOrderConfirmed || false,
              orgCenterId: stockItem?.orgCenterId || '',
            }
            newOrderPlanList.push(orderPlan)
          })
        }
      })

      setOrderPlanList([...orderPlanListFiltered, ...newOrderPlanList])
      updateTransportSummaryList([...orderPlanListFiltered, ...newOrderPlanList])

      showResultMessage(setResultMessage, {
        status: 'success',
        message: eventClick ? `${isRecDate.toLocaleDateString('ja-JP')}の車両割り当てを再計算しました` : ''
      })
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error calculating transport:', error)
      showResultMessage(setResultMessage, {
        status: 'error',
        message: '車両割り当ての再計算でエラーが発生しました。'
      })
    }
    setLoadingState(false)
  }, [stockList, orderPlanRecDates, stockDataResponse, transportTypeSearch, minLoadCapacity, transportTypeData, calculateTransportLot, setHasGridEdited])

  const handleFileDownload = () => {
    // Validate if column definitions are ready
    if (!downloadColDef || downloadColDef.length === 0) {
      showResultMessage(setResultMessage, {
        status: 'error',
        message: 'カラム定義が読み込まれていません。しばらくお待ちください。'
      })
      return
    }

    // Validate if there's data to download
    if (!orderPlanList || orderPlanList.length === 0) {
      showResultMessage(setResultMessage, {
        status: 'error',
        message: 'ダウンロードするデータがありません。'
      })
      return
    }

    try {
      setDownloadGridRows(generateDownloadData())
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Download error:', error)
      showResultMessage(setResultMessage, {
        status: 'error',
        message: 'ダウンロード処理中にエラーが発生しました。'
      })
    }
  }

  const generateDownloadData = (): any[] => {
    if (!orderPlanList || orderPlanList.length === 0) {
      return []
    }

    return orderPlanList.map(row => ({
      ...row,
      maxWeight: !row.maxWeight ? '' : row.maxWeight,
      maxPalletCount: !row.maxPalletCount ? '' : row.maxPalletCount,
    }))
  }

  // Transport type dialog handlers
  const handleTransportTypeDialogClose = useCallback(() => {
    setTransportTypeDialogOpen(false)
    setSelectedRecDate(null)
    setSelectedTransportLotNo(null)
    setSelectedTransportTypeId(null)
  }, [])

  const handleTransportTypeChange = useCallback((newTransportTypeId: number) => {
    if (!selectedRecDate || !selectedTransportLotNo || !orderPlanList) {
      return
    }
    const systemDate = normalizeDate(loginUserInfo?.systemDate)

    // Find the transport type details
    const selectedTransportType = transportTypeSearch?.list?.find(
      (type) => type.transportTypeId === newTransportTypeId
    )

    if (!selectedTransportType) {
      showResultMessage(setResultMessage, {
        status: 'warning',
        message: '選択された輸送種類が見つかりません'
      })
      return
    }

    // Update all items in the transport lot
    const updatedOrderPlanList = orderPlanList.map((item) => {
      if (
        item.recDate &&
        item.transportLotNo === selectedTransportLotNo &&
        isEqualDate(item.recDate, selectedRecDate)
      ) {
        if (isEqualDate(systemDate, item.orderDate)) {
          setHasGridEdited(true)
        }
        return {
          ...item,
          orderTransportTypeId: newTransportTypeId,
          transportTypeName: selectedTransportType.transportTypeName || '',
          maxWeight: selectedTransportType.maxWeight || 0,
          maxPalletCount: selectedTransportType.maxPalletCount || 0,
          isEdited: true,
          editedFields: (() => {
            const fields = new Set(item.editedFields || [])
            fields.add('orderTransportTypeId')
            return fields
          })(),
        }
      }
      return item
    })

    setOrderPlanList(updatedOrderPlanList)
    updateTransportSummaryList(updatedOrderPlanList)
    setTransportTypeDialogOpen(false)
    showResultMessage(setResultMessage, {
      status: 'success',
      message: '輸送種類を変更しました'
    })
    
    // Reset selection
    setSelectedRecDate(null)
    setSelectedTransportLotNo(null)
    setSelectedTransportTypeId(null)
  }, [selectedRecDate, selectedTransportLotNo, orderPlanList, transportTypeSearch, setHasGridEdited])

  // Row class rules
  const rowClassRules: { [className: string]: (params: RowClassParams) => boolean } = {
    'todays-order-cell': (params) => {
      if (params.node.level !== 0) return false
      const leaves = (params.node as any).allLeafChildren || (params.node as any).childrenAfterFilter || []
      const hasConfirmed = leaves.some((child: any) => child?.data?.isOrderConfirmed === true)
      const hasAssignedLot = leaves.some((child: any) => !!child?.data?.transportLotNo)
      return hasConfirmed && hasAssignedLot
    },
    'warning-cell-red': (params) => {
      if (params.node.level === 0) {
        if (
          centerSupplierInfo?.minOrderQuantityOnce &&
          params.node.aggData?.lotQuantity !== '-' &&
          params.node.aggData?.lotQuantity != null
        ) {
          return Number(params.node.aggData.lotQuantity) < Number(centerSupplierInfo.minOrderQuantityOnce)
        }
      }

      if (params.node.level === 1) {
        if (params.node.aggData?.lotItemWeight !== '-' && params.node.aggData?.lotItemWeight) {
          const match = String(params.node.aggData.lotItemWeight).match(/\(([\d.]+)%\)/)
          return match ? parseFloat(match[1]) > 100 : false
        }
      }
      return false
    },

    'warning-cell-blue': (params) => {
      if(params.node.level === 1 &&
      params.node.aggData?.lotItemWeight !== '-' &&
      !!params.node.aggData?.lotItemWeight) {
        // Check load percentage
        const weightMatch = String(params.node.aggData.lotItemWeight).match(/\(([\d.]+)%\)/)
        const loadPercentage = weightMatch ? parseFloat(weightMatch[1]) : 0
        // Get max pallet count from first child
        const firstChild = params.node.childrenAfterFilter?.[0]
        const maxPalletCount = firstChild?.data?.maxPalletCount || 0
        // Check pallet quantity
        let integerPallets = 0
        let remainPallets = 0

        params.node.childrenAfterFilter?.forEach((child: any) => {
          const childPallets = calculateLotQuantityPallet(
            child.data?.lotQuantity,
            child.data?.maxPalletQuantity
          )
          if (Number.isInteger(childPallets)) {
            integerPallets += childPallets
          }
          else {
            integerPallets += Math.floor(childPallets)
            remainPallets = 1
          }
        })
        const totalPallets = integerPallets + remainPallets

        // Condition: load percentage is less than minLoadCapacity AND pallet quantity is less than maxPalletCount
        return loadPercentage < minLoadCapacity && totalPallets < maxPalletCount
      }
      return false
    },

    'updated-cell': (params) => params.data?.isOrderDateChange
  }

  const updateTransportSummaryList = useCallback(
    (orderPlanArr: OrderPlan[]) => {
      if (!orderPlanArr) return;
  
      // Get current systemDate
      const systemDate = normalizeDate(loginUserInfo?.systemDate ?? Date.now())!;
      systemDate.setHours(0, 0, 0, 0);
  
      // Determine the time range: systemDate -7 to +13
      const startDate = subDays(systemDate, 7)
      let endDate: Date
      if(optimizeRangeEndDate) {
        const endDays = calculateDaysDifference(systemDate, optimizeRangeEndDate)
        endDate = addDays(systemDate, endDays)
      } else {
        endDate = addDays(systemDate, 13)
      }
  
      // Get all dates within the time range from both orderPlanArr and stockList
      const allDates = new Set<string>();
      
      // Add dates from orderPlanArr
      orderPlanArr.forEach((plan) => {
        if (plan.recDate) {
          const normalizedDate = normalizeDate(plan.recDate);
          if (!normalizedDate) return;
          
          // Check if date is within range
          if (normalizedDate >= startDate && normalizedDate <= endDate) {
            const dateKey = normalizedDate.toLocaleDateString('sv-SE');
            allDates.add(dateKey);
          }
        }
      });
      
      // Also add dates from stockList to ensure we don't miss any dates
      stockList?.forEach((stock) => {
        if (stock.recDate) {
          const normalizedDate = normalizeDate(stock.recDate);
          if (!normalizedDate) return;
          
          // Check if date is within range
          if (normalizedDate >= startDate && normalizedDate <= endDate) {
            const dateKey = normalizedDate.toLocaleDateString('sv-SE');
            allDates.add(dateKey);
          }
        }
      });
  
      // Calculate transportSummary for each day in the time range
      const newTransportSummaryData: TransportSummaryData[] = [];
      Array.from(allDates).sort().forEach((dateKey) => {
        const recDate = normalizeDate(dateKey);

        // Use stockList to get rectypeName instead of stockDataResponse?.daylist
        // Find the first stock item for this date to get rectypeName
        const stockItemForDate = stockList?.find(
          (item) => item.recDate && isEqualDate(item.recDate, recDate)
        );
        const rectypeName = stockItemForDate?.rectypeName || '';
        
        const plansForDate = orderPlanArr.filter(
          (plan) => plan.recDate && isEqualDate(plan.recDate, recDate)
        );
        let totalWeight = 0
        let isOrderDateToday = false
        const uniqueTransportLots = new Set<number>()
        plansForDate.forEach((plan) => {
          if (isEqualDate(plan.orderDate, systemDate)) {
            isOrderDateToday = true
          }
          // Only calculate when transportLotNo > 0
          if (plan.transportLotNo && plan.lotItemWeight) {
            totalWeight += plan.lotItemWeight;
            uniqueTransportLots.add(plan.transportLotNo);
          }
        });
        newTransportSummaryData.push({
          date: dateKey,
          totalWeight: Number(totalWeight.toFixed(3)),
          totalVehicles: uniqueTransportLots.size,
          rectypeName: mapRectypeNameToUI(rectypeName),
          isOrderDateToday,
        });
      });
  
      // Keep old items that are not in allDates
      setTransportSummaryList((prevList = []) => {
        const oldItems = prevList.filter(item => !allDates.has(item.date));
        return [...oldItems, ...newTransportSummaryData].sort((a, b) => a.date.localeCompare(b.date));
      });
    },
    [loginUserInfo, setTransportSummaryList, stockList]
  );

  // Show messages from API results
  useEffect(() => {
    if (stockDataResult?.status) {
      setResultMessage(stockDataResult)
    }
  }, [stockDataResult])

  useEffect(() => {
    if (orderPlanUpdateResult?.status) {
      setResultMessage(orderPlanUpdateResult)
    }
  }, [orderPlanUpdateResult])

  // Render
  return (
    <CompactAlertBox>
      <MasterSearchFrom
        result={resultMessage}
        onDoUpdate={async (): Promise<void> => { await handleRegister() }}
        onReload={onReload}
        onFileDownLoad={handleFileDownload}
        setDownloadMode={setDownloadMode}
        discardChangeDialogOpen={discardChangeDialogOpen}
        hasDataEdited={hasGridEdited}
        importRequiredFields={['transportTypeName', 'maxWeight', 'maxPalletCount']}
        headerJp={['輸送手段名', '最大積載重量（ｔ）', 'パレット最大積載枚数']}
        lastUpdateDatetime={searchFormLastUpdateDatetime}
        isImport={false}
        onOrderRecommend={handleOrderRecommend}
        isOrderRecommend={loginUserInfo?.companyId === initSearchParam.companyId}
        mismatchedStockItemCodes={ isLoading ? [] : mismatchedStockItemCodes}
        updateButtonText="本日発注分の確定"
        optimizeButtonText="本日発注分の確定と発注推奨数再計算"
      >
        <SearchCondition
          searchParam={initSearchParam}
          onSearch={doSearch}
          setTransportType={setTransportTypeData}
          initialTransportType={initialTransportType}
          hasDataEdited={hasGridEdited}
          setDiscardChangeDialogOpen={setDiscardChangeDialogOpen}
          searchTrigger={searchTrigger}
          setMinLoadCapacity={setMinLoadCapacity}
          setCenterSupplierInfo={setCenterSupplierInfo}
          setOptimizeLoadFactorLogic={setOptimizeLoadFactorLogic}
          onOptimizeLoadFactor={onOptimizeLoadFactor}
          onTransportLot={() => onTransportLot(true)}
          transportTypeSearch={transportTypeSearch}
          initialMinLoadCapacity={minLoadCapacity}
          onValidateAndExecute={handleValidateAndExecute}
        />
      </MasterSearchFrom>
      <FileDownloadData fileName="発注計画積載率最適化" downloadData={downloadGridRows} downloadMode={downloadMode} columnDefs={downloadColDef} />

      <Accordion
          disableGutters
          expanded={isOpen}
          onChange={() => setIsOpen(prev => !prev)}>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls="panel1-content"
          id="panel1-header"
          sx={{
            minHeight: 40,
            height: 40,
            '&.Mui-expanded': { minHeight: 40, height: 40 },
            '.MuiAccordionSummary-content': { margin: 0 },
          }}
          >
          <Typography variant="largeBold">車両予定</Typography>
        </AccordionSummary>
        <AccordionDetails>
        <TransportSummary
          data={transportSummaryList} 
          isLoading={isLoading || loadingState || isLoadingTransportLot || isLoadingOptimizeData || isOptimizingOrderQuantity} />
        </AccordionDetails>
      </Accordion>
      <SearchBox 
        gridRef={gridSearch.searchBoxProps.gridRef}
        findSearchValue={gridSearch.searchBoxProps.findSearchValue}
        setFindSearchValue={gridSearch.searchBoxProps.setFindSearchValue}
        activeMatchNum={gridSearch.searchBoxProps.activeMatchNum}
        onFindChanged={gridSearch.searchBoxProps.onFindChanged}
        isVisible={gridSearch.searchBoxProps.isVisible}
        onHide={gridSearch.searchBoxProps.onHide}
      />
      <DataList
        orderPlanList={orderPlanList ?? []}
        isLoading={isLoading || loadingState || isLoadingTransportLot || isLoadingOptimizeData || isOptimizingOrderQuantity}
        setDownloadColumnDefs={setDownloadColDef}
        onOrderPlanChange={onOrderPlanChange}
        onDoUpdateForRecDate={onDoUpdateForRecDate}
        onTransportLotForRecDate={onTransportLotForRecDate}
        onOptimizeLoadFactorForRecDate={onOptimizeLoadFactorForRecDate}
        onTransportTypeChange={onTransportTypeChange}
        onChangeArrivalNextDay={onChangeArrivalNextDay}
        onChangeArrivalPrevDay={onChangeArrivalPrevDay}
        rowClassRules={rowClassRules}
        isTransportSummaryOpen={isOpen}
        gridRef={gridSearch.gridRef}
        findSearchValue={gridSearch.gridSearchProps.findSearchValue}
        onFindChanged={gridSearch.gridSearchProps.onFindChanged}
        isSearchVisible={gridSearch.searchBoxProps.isVisible}
      />
      
      {/* Transport Type Change Dialog */}
      <Dialog
        open={transportTypeDialogOpen}
        onClose={handleTransportTypeDialogClose}
        aria-labelledby="transport-type-dialog-title"
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle id="transport-type-dialog-title">
          輸送種類変更
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            {selectedRecDate && selectedTransportLotNo && (
              <Typography variant="body2" sx={{ mb: 2 }}>
                入荷日: {selectedRecDate.toLocaleDateString('ja-JP')} / 輸送ロット: {selectedTransportLotNo}
              </Typography>
            )}
            <FormControl fullWidth>
              <InputLabel id="transport-type-select-label">使用車両</InputLabel>
              <Select
                labelId="transport-type-select-label"
                id="transport-type-select"
                value={selectedTransportTypeId || ''}
                label="使用車両"
                onChange={(e) => setSelectedTransportTypeId(Number(e.target.value))}
              >
                {transportTypeSearch?.list
                  ?.filter((type) => type.deleteFlg === false)
                  ?.map((type) => (
                    <MenuItem key={type.transportTypeId} value={type.transportTypeId || ''}>
                      [{type.transportTypeId}]{type.transportTypeName} ({type.maxWeight}t,{type.maxPalletCount}PL)
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleTransportTypeDialogClose} color="info">
            キャンセル
          </Button>
          <Button 
            onClick={() => selectedTransportTypeId && handleTransportTypeChange(selectedTransportTypeId)} 
            color="primary" 
            variant="contained"
            disabled={!selectedTransportTypeId}
          >
            変更
          </Button>
        </DialogActions>
      </Dialog>
    </CompactAlertBox>
  )
}

export default OrderPlanTransportLot
